<template>
  <div :class="['content-editor', { 'ce-mobile': isMobile }]">
    <!-- Gray backdrop -->
    <div class="ce-backdrop" @click="$emit('close')"></div>

    <!-- Dialog wrapper for positioning X -->
    <div class="ce-dialog-wrap">
      <button class="ce-close" @click="$emit('close')"><i class="fa fa-times"></i></button>
      <div class="ce-dialog">

      <!-- Left panel: light gray -->
      <div class="ce-left">
        <div class="ce-left-scroll">
          <div :class="['ce-preview-item', { active: activePanel === 'cover' }]">
            <div class="ce-preview-item__wrap" @click="switchPanel('cover')">
              <cover-holder :src="coverImage" :height="141.1"/>
              <div class="ce-preview-item__hover">
                <template v-if="activePanel === 'cover'">
                  <a class="ce-preview-link" :href="coverPreviewUrl" target="_blank" @click.stop>
                    <i class="fa fa-search"></i> {{$t('editor.viewLarge')}}
                  </a>
                </template>
                <template v-else>
                  <span class="ce-hover-btn" @click.stop="switchPanel('cover')">
                    <i class="fa fa-paint-brush"></i> {{$t('editor.changeCover')}}
                  </span>
                </template>
              </div>
            </div>
            <div class="ce-preview-item__label"><i class="fa fa-book"></i> {{$t('cover.tabCover')}}</div>
          </div>
          <div :class="['ce-preview-item', { active: activePanel === 'insert' }]">
            <div class="ce-preview-item__wrap" @click="switchPanel('insert')">
              <cover-holder :src="insertPageImage" :height="141.1"/>
              <div class="ce-preview-item__hover">
                <span>{{$t('editor.changeInsert')}}</span>
              </div>
            </div>
            <div class="ce-preview-item__label"><i class="fa fa-bookmark"></i> {{$t('cover.tabInsert')}}</div>
          </div>
          <div :class="['ce-preview-item', { active: activePanel === 'layout' }]">
            <div class="ce-preview-item__wrap" @click="switchPanel('layout')">
              <cover-holder :src="layoutImage" :height="141.1"/>
              <div class="ce-preview-item__hover">
                <span>{{$t('editor.changeLayout')}}</span>
              </div>
            </div>
            <div class="ce-preview-item__label"><i class="fa fa-th"></i> {{$t('cover.tabTypeset')}}</div>
          </div>
        </div>
      </div>

      <!-- Right panel: white -->
      <div class="ce-right">
        <!-- Tab bar -->
        <div class="ce-tabs">
          <div class="ce-tabs__list">
            <div :class="['ce-tab', { active: isDesignPanel }]"
                 @click="switchPanel('cover')">
              <i class="fa fa-paint-brush"></i> {{$t('editor.tabDesign')}}
            </div>
            <div :class="['ce-tab', { active: activePanel === 'content' }]"
                 @click="switchPanel('content')">
              <i class="fa fa-pencil"></i> {{$t('editor.tabEdit')}}
            </div>
            <div :class="['ce-tab', { active: activePanel === 'sources' }]"
                 @click="switchPanel('sources')">
              <i class="fa fa-cog"></i> {{$t('editor.tabSources')}}
            </div>
          </div>
          <button class="btn primary ce-save" @click="onSavePreview">
            <i class="fa fa-book"></i> {{$t('editor.savePreview')}}
          </button>
        </div>

        <!-- Mobile nav bar -->
        <div class="ce-mobile-nav">
          <div class="ce-mobile-nav__left">
            <button class="ce-mobile-nav__btn" @click="showMobileDesign = !showMobileDesign; showMobileEdit = false">
              <i class="fa fa-paint-brush"></i> {{$t('editor.tabDesign')}}
            </button>
            <div class="ce-mobile-nav__overlay" v-if="showMobileDesign" @click="showMobileDesign = false"></div>
            <div class="ce-mobile-nav__pop ce-mobile-nav__pop--left" v-if="showMobileDesign">
              <button @click="switchPanel('cover'); showMobileDesign = false"><i class="fa fa-book"></i> {{$t('cover.tabCover')}}</button>
              <button @click="switchPanel('insert'); showMobileDesign = false"><i class="fa fa-bookmark"></i> {{$t('cover.tabInsert')}}</button>
              <button @click="switchPanel('layout'); showMobileDesign = false"><i class="fa fa-th"></i> {{$t('cover.tabTypeset')}}</button>
            </div>
          </div>
          <button class="btn primary btn-sm ce-mobile-save" @click="onSavePreview">
            {{$t('editor.savePreview')}}
          </button>
          <div class="ce-mobile-nav__right">
            <button class="ce-mobile-nav__btn" @click="showMobileEdit = !showMobileEdit; showMobileDesign = false">
              <i class="fa fa-pencil"></i> {{$t('editor.tabEdit')}}
            </button>
            <div class="ce-mobile-nav__overlay" v-if="showMobileEdit" @click="showMobileEdit = false"></div>
            <div class="ce-mobile-nav__pop ce-mobile-nav__pop--right" v-if="showMobileEdit">
              <button @click="switchPanel('content'); showMobileEdit = false"><i class="fa fa-pencil"></i> {{$t('editor.tabEdit')}}</button>
              <button @click="switchPanel('sources'); showMobileEdit = false"><i class="fa fa-cog"></i> {{$t('editor.tabSources')}}</button>
            </div>
          </div>
        </div>

        <!-- Cover design panel -->
        <div class="ce-design-panel" v-if="activePanel === 'cover'">
          <div class="ce-design-scroll">
            <div class="ce-manage-section">
              <div class="ce-manage-section__title"><i class="fa fa-book"></i> {{$t('cover.tabCover')}}</div>
              <div class="ce-cover-top">
                <div class="ce-cover-top__preview" v-if="isMobile">
                  <cover-holder :src="coverImage" :height="141.1"/>
                </div>
                <div class="ce-cover-top__left">
                  <div class="form-group">
                    <label>{{$t('cover.labelBookTitle')}}<span class="text-muted text-mini">（{{$t('cover.labelMaxCount', { count: 25 })}}）</span></label>
                    <input type="text" v-model.lazy="designData.title" @change="saveDesign" class="form-control" maxlength="25"
                           :placeholder="$t('cover.labelBookTitle')">
                  </div>
                  <div class="form-group">
                    <label>{{$t('cover.labelBookAuthor')}}<span class="text-muted text-mini">（{{$t('cover.labelMaxCount', { count: 15 })}}）</span></label>
                    <input type="text" v-model.lazy="designData.author" @change="saveDesign" class="form-control" maxlength="15"
                           :placeholder="$t('cover.labelBookAuthor')">
                  </div>
                  <div class="form-group" v-if="isColorfulCover && !isMobile">
                    <label>{{$t('cover.labelCoverSelect')}}</label>
                    <color-list :coverColor="designData.cover.color" @change="onChangeColor"/>
                  </div>
                </div>
                <div class="ce-cover-top__right">
                  <label>{{$t('editor.coverImage')}}</label>
                  <div class="ce-cover-thumb">
                    <div class="ce-cover-thumb__img" :style="{backgroundImage: 'url(' + coverThumbUrl + ')'}"></div>
                    <div class="ce-cover-thumb__hover">
                      <uploader @change="onUploadCover" :mixed="false">
                        <span class="ce-hover-btn"><i class="fa fa-camera"></i> {{coverImageUrl ? $t('editor.replaceImage') : $t('editor.uploadImage')}}</span>
                      </uploader>
                      <span class="ce-hover-btn ce-hover-btn--reset" v-if="coverImageUrl && coverImageUrl !== originalCoverImageUrl" @click="resetCoverImage">
                        <i class="fa fa-undo"></i> {{$t('editor.resetImage')}}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="ce-cover-bottom-row" v-if="isMobile">
                <div class="ce-cover-bottom-row__color" v-if="isColorfulCover">
                  <label>{{$t('cover.labelCoverSelect')}}</label>
                  <color-list :coverColor="designData.cover.color" @change="onChangeColor"/>
                </div>
                <div class="ce-cover-bottom-row__photo">
                  <label>Cover Photo</label>
                  <div class="ce-cover-thumb ce-cover-thumb--mobile">
                    <div class="ce-cover-thumb__img" :style="{backgroundImage: 'url(' + coverThumbUrl + ')'}"></div>
                    <div class="ce-cover-thumb__hover">
                      <uploader @change="onUploadCover" :mixed="false">
                        <span class="ce-hover-btn"><i class="fa fa-camera"></i></span>
                      </uploader>
                    </div>
                  </div>
                </div>
              </div>
              <cover-type :value="designData" @change="onChangeCover"/>
            </div>
          </div>
        </div>

        <!-- Insert Page design panel -->
        <div class="ce-design-panel" v-else-if="activePanel === 'insert'">
          <div class="ce-design-scroll">
            <div class="ce-manage-section">
              <div class="ce-manage-section__title"><i class="fa fa-bookmark"></i> {{$t('cover.tabInsert')}}</div>
              <p class="ce-panel-subtitle">{{$t('editor.insertSubtitle')}}</p>
              <insert-page :insert="designData.insertPage" @change="onChangeInsert"/>
            </div>
          </div>
        </div>

        <!-- Layout design panel -->
        <div class="ce-design-panel" v-else-if="activePanel === 'layout'">
          <div class="ce-design-scroll">
            <div class="ce-manage-section">
              <div class="ce-manage-section__title"><i class="fa fa-th"></i> {{$t('cover.tabTypeset')}}</div>
              <typeset-type :typeset="designData.typeset" @change="onChangeTypeset"/>
            </div>
          </div>
        </div>

        <!-- Manage Contents panel: content sources -->
        <div class="ce-sources-panel" v-else-if="activePanel === 'sources'">
          <div class="ce-sources-scroll">
            <!-- Connect more content -->
            <div class="ce-sources-connect">
              <span class="ce-sources-connect__label">{{$t('editor.connectMore')}}</span>
              <div class="ce-sources-connect__icons">
                <a :href="fbAuthLink" class="ce-source-icon ce-source-icon--fb" title="Facebook">
                  <svg viewBox="0 0 24 24" fill="#fff" width="22" height="22">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
              </div>
            </div>
            <hr class="ce-sources-divider">

            <!-- Loading -->
            <div class="content-loading" v-if="!sourcesLoaded">
              <div class="loading-icon" style="transform: scale(0.8)"></div>
            </div>

            <!-- 新版：左侧 source 列表 + 右侧 year/month grid -->
            <div class="ce-sources-layout" v-if="sourcesLoaded && contentSources.length">
              <!-- 左侧 source 卡片列 —— 沿用原 ce-source-card 视觉（badge + header + 分隔 + 整行 category） -->
              <div class="ce-sources-list">
                <div v-for="group in contentSources" :key="group.id" class="ce-source-card">
                  <div class="ce-source-card__badge" :class="'ce-source-card__badge--' + group.iconType">
                    <svg v-if="group.iconType === 'fb'" viewBox="0 0 24 24" fill="#fff" width="18" height="18">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                    <i v-else-if="group.icon" :class="group.icon"></i>
                    <img v-else-if="group.iconImage" :src="group.iconImage" class="ce-source-card__badge-img">
                    <span v-else>{{group.iconText}}</span>
                  </div>
                  <div class="ce-source-card__body">
                    <div class="ce-source-card__header">
                      <div class="ce-source-card__user" v-if="group.avatar">
                        <img :src="group.avatar" class="ce-source-card__avatar"
                             @error="group.avatar = ''">
                        <div>
                          <strong class="ce-source-card__name">{{group.name}}</strong>
                          <div class="ce-source-card__meta" v-if="group.dateRange">{{group.dateRange}}</div>
                        </div>
                      </div>
                      <div class="ce-source-card__info" v-else>
                        <strong class="ce-source-card__name" v-if="group.name">{{group.name}}</strong>
                        <div class="ce-source-card__meta" v-if="group.dateRange">{{group.dateRange}}</div>
                      </div>
                      <button class="ce-source-card__action"
                              v-if="group.actionLabel"
                              @click.stop="handleSourceAction(group)">
                        {{group.actionLabel}}
                      </button>
                    </div>
                    <hr class="ce-source-card__divider">
                    <div class="ce-source-card__categories">
                      <div v-for="cat in group.categories" :key="cat.type"
                           :class="['ce-source-category', {
                             'ce-source-category--active': activeSourceKey === categoryToKey(group, cat)
                           }]"
                           @click="selectCategory(group, cat)">
                        <span class="ce-source-category__dot"
                              :class="{ 'is-on': catHasContent(group, cat) }"></span>
                        <span class="ce-source-category__label">{{cat.label}}<span class="ce-source-category__sep">|</span><span class="ce-source-category__count">{{cat.count}}</span>{{cat.unit}}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- 右侧 year/month grid -->
              <div class="ce-months-pane">
                <div v-if="!activeSourceKey" class="ce-months-pane__empty">
                  ← {{$t('editor.selectSourceFirst')}}
                </div>
                <div v-else>
                  <div class="ce-months-pane__header">
                    <div>
                      <strong>{{activeSourceLabel}}</strong>
                      <span class="ce-months-pane__hint">{{$t('editor.monthsGridHint')}}</span>
                    </div>
                    <a class="ce-months-pane__toggle-all"
                       v-if="!monthsGridLoading && Object.keys(monthsGrid).length"
                       @click="toggleAllCurrent">
                      {{ allCurrentChecked ? $t('editor.hideAllShortcut') : $t('editor.selectAllShortcut') }}
                    </a>
                  </div>
                  <div class="ce-months-pane__loading" v-if="monthsGridLoading">
                    <div class="loading-icon" style="transform: scale(0.6)"></div>
                  </div>
                  <div v-else-if="!Object.keys(monthsGrid).length" class="ce-months-pane__empty">
                    {{$t('editor.noMonthsForSource')}}
                  </div>
                  <div v-else class="ce-years-grid">
                    <div v-for="year in sortedYears" :key="year" class="ce-year-block">
                      <label class="ce-year-checkbox">
                        <input type="checkbox"
                               :checked="yearChecked(year)"
                               :indeterminate.prop="yearIndeterminate(year)"
                               @change="toggleYear(year)">
                        <span>{{year}}{{$t('preview.yearSuffix')}} <em class="ce-year-block__count">{{yearTotal(year)}}{{activeUnit}}</em></span>
                      </label>
                      <div class="ce-months-row">
                        <label v-for="m in sortedMonths(year)" :key="m"
                               class="ce-month-chip"
                               :class="{ checked: monthEffectiveChecked(year, m) }">
                          <input type="checkbox"
                                 :checked="monthEffectiveChecked(year, m)"
                                 @change="toggleMonth(year, m)">
                          <span class="ce-month-chip__label">{{monthDisplay(m)}}</span>
                          <span class="ce-month-chip__sep">|</span>
                          <span class="ce-month-chip__count">{{monthsGrid[year][m].total}}{{activeUnit}}</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- Empty state -->
            <div class="content-empty" v-else-if="sourcesLoaded && !contentSources.length">
              <i class="fa fa-plug"></i>
              <p>{{$t('editor.connectMore')}}</p>
            </div>
          </div>
        </div>

        <!-- Content editing panel (original) -->
        <div class="ce-body" v-else-if="activePanel === 'content'">
          <div class="ce-timeline" v-if="timeLine">
            <time-line :chapters="timeLine"
                       :activeYear="activeYear"
                       :activeMonth="activeMonth"
                       :editable="true"
                       :key="timelineKey"
                       @changeMonth="onChangeMonth"
                       @hideMonth="onHideMonth"
                       @showMonth="onShowMonth"
                       @hideYear="onHideYear"
                       @showYear="onShowYear"
                       @addContent="smartAddPatch"/>
          </div>
          <div class="ce-content">
        <!-- Month header -->
        <div class="content-header" v-if="currentMonthLabel">
          <div class="content-header__info">
            <strong>{{currentMonthLabel}}</strong>
            <span :class="['content-tab', { active: viewMode === 'posts' }]" @click="viewMode = 'posts'">
              {{items.length}}{{$t('editor.postCount')}}
            </span>
            <span :class="['content-tab content-tab--deleted', { active: viewMode === 'deleted' }]" @click="viewMode = 'deleted'" v-if="deletedItems.length">
              {{$t('editor.deletedCount', { count: deletedItems.length })}}
            </span>
          </div>
          <div class="content-header__actions">
            <template v-if="viewMode === 'posts'">
              <template v-if="batchMode">
                <span class="action-link danger" @click="batchDelete" v-if="selectedItems.size">
                  <i class="fa fa-trash-o"></i>
                  {{$t('editor.deleteSelected')}} ({{selectedItems.size}})
                </span>
                <span class="action-link" @click="cancelBatch">
                  {{$t('editor.cancel')}}
                </span>
              </template>
              <template v-else>
                <span class="action-link" @click="updateCurrentMonth" v-if="canUpdateCurrentMonth">
                  <i class="fa fa-refresh"></i>
                  {{$t('editor.updateMonth')}}
                </span>
                <span class="action-link" @click="hideCurrentMonth" v-if="currentMonth">
                  {{$t('editor.hideThisMonth')}}
                </span>
                <span class="action-link" @click="enterBatchMode">
                  {{$t('editor.batchDelete')}}
                </span>
              </template>
            </template>
            <template v-else>
              <template v-if="batchMode">
                <span class="action-link primary" @click="batchRecover" v-if="selectedItems.size">
                  <i class="fa fa-undo"></i>
                  {{$t('editor.recoverSelected')}} ({{selectedItems.size}})
                </span>
                <span class="action-link" @click="cancelBatch">
                  {{$t('editor.cancel')}}
                </span>
              </template>
              <span class="action-link" @click="enterBatchMode" v-else>
                {{$t('editor.batchRecover')}}
              </span>
            </template>
          </div>
        </div>

        <!-- Loading -->
        <div class="content-loading" v-if="loading">
          <div class="loading-icon" style="transform: scale(0.8)"></div>
        </div>

        <!-- Item list (posts view) -->
        <div class="content-list" v-else-if="viewMode === 'posts' && items.length" ref="contentList">
          <template v-for="(item, index) in items">
            <!-- Add button between items -->
            <div class="add-divider" :key="'add-' + index" v-if="index > 0 && addingAt !== index">
              <div class="add-divider__line"></div>
              <button class="add-divider__btn" @click="startAdding(index)"
                      :title="$t('editor.addManually')">
                <i class="fa fa-plus"></i>
              </button>
            </div>

            <!-- Inline add form -->
            <div class="inline-patch" :key="'patch-' + index" v-if="addingAt === index">
              <div class="inline-patch__header">{{$t('editor.addingContent')}}</div>
              <div class="inline-patch__body">
                <div class="row form-group">
                  <div class="col-xs-8">
                    <input class="form-control" type="date" v-model="patchDate">
                  </div>
                  <div class="col-xs-4">
                    <input class="form-control" type="time" v-model="patchTime">
                  </div>
                </div>
                <textarea class="form-control" :placeholder="$t('editor.patchPlaceholder')"
                          v-model="patchContent"></textarea>
                <div class="inline-patch__images">
                  <div class="patch-img" v-for="(img, i) in patchImages" :key="img[0]" @click="patchImages.splice(i, 1)">
                    <div class="patch-img__inner" :style="{backgroundImage: 'url(' + img[1] + ')'}"></div>
                    <div class="patch-img__del"><i class="fa fa-times"></i></div>
                  </div>
                  <div class="patch-img upload-btn">
                    <uploader @change="uploadPatchImage" :mixed="true"/>
                  </div>
                </div>
                <p class="text-muted text-mini">{{$t('editor.imageMinSize')}}</p>
              </div>
              <div class="inline-patch__footer">
                <button class="btn plant" @click="cancelAdding">{{$t('editor.cancel')}}</button>
                <button class="btn primary" @click="saveNewItem(index)">{{$t('editor.confirm')}}</button>
              </div>
            </div>

            <!-- Post card -->
            <editor-card :item="item" :key="item.itemId"
                         :active="activeItemId === item.itemId"
                         :batchMode="batchMode"
                         :selected="selectedItems.has(item.itemId)"
                         sourceOverride=""
                         @edit="activeItemId = item.itemId"
                         @cancel="activeItemId = ''"
                         @delete="deleteItem(item)"
                         @save="saveItem($event, item)"
                         @toggle-select="toggleSelect(item.itemId)"/>
          </template>

          <!-- Add button at bottom -->
          <div class="add-divider" v-if="addingAt !== items.length">
            <div class="add-divider__line"></div>
            <button class="add-divider__btn" @click="startAdding(items.length)"
                    :title="$t('editor.addManually')">
              <i class="fa fa-plus"></i>
            </button>
          </div>
          <div class="inline-patch" v-if="addingAt === items.length">
            <div class="inline-patch__header">{{$t('editor.addingContent')}}</div>
            <div class="inline-patch__body">
              <div class="row form-group">
                <div class="col-xs-8">
                  <input class="form-control" type="date" v-model="patchDate">
                </div>
                <div class="col-xs-4">
                  <input class="form-control" type="time" v-model="patchTime">
                </div>
              </div>
              <textarea class="form-control" :placeholder="$t('editor.patchPlaceholder')"
                        v-model="patchContent"></textarea>
              <div class="inline-patch__images">
                <div class="patch-img" v-for="(img, i) in patchImages" :key="img[0]" @click="patchImages.splice(i, 1)">
                  <div class="patch-img__inner" :style="{backgroundImage: 'url(' + img[1] + ')'}"></div>
                  <div class="patch-img__del"><i class="fa fa-times"></i></div>
                </div>
                <div class="patch-img upload-btn">
                  <uploader @change="uploadPatchImage" :mixed="true"/>
                </div>
              </div>
              <p class="text-muted text-mini">{{$t('editor.imageMinSize')}}</p>
            </div>
            <div class="inline-patch__footer">
              <button class="btn plant" @click="cancelAdding">{{$t('editor.cancel')}}</button>
              <button class="btn primary" @click="saveNewItem(items.length)">{{$t('editor.confirm')}}</button>
            </div>
          </div>
        </div>

        <!-- Deleted items list -->
        <div class="content-list deleted-list" v-else-if="viewMode === 'deleted' && deletedItems.length">
          <template v-for="item in deletedItems">
            <editor-card :key="item.itemId"
                         :item="item"
                         :active="false"
                         :batchMode="batchMode"
                         :selected="selectedItems.has(item.itemId)"
                         sourceOverride=""
                         :deleted="true"
                         @recover="recoverItem(item)"
                         @toggle-select="toggleSelect(item.itemId)"/>
          </template>
        </div>

        <!-- Empty deleted state -->
        <div class="content-empty" v-else-if="viewMode === 'deleted' && !deletedItems.length">
          <i class="fa fa-trash-o"></i>
          <p>{{$t('editor.emptyDeleted')}}</p>
        </div>

        <!-- Empty state -->
        <div class="content-empty" v-else-if="!loading && currentMonthLabel && viewMode === 'posts'">
          <i class="fa fa-folder-open-o"></i>
          <p>{{$t('editor.emptyMonth')}}</p>
        </div>

        <!-- No month selected -->
        <div class="content-empty" v-else-if="!loading">
          <i class="fa fa-hand-pointer-o"></i>
          <p>{{$t('editor.selectMonth')}}</p>
        </div>
          </div>
        </div>
      </div>
      </div>
      <!-- Mobile: Timeline FAB (only in content panel) -->
      <div class="ce-mobile-toc" v-if="activePanel === 'content'">
        <div class="ce-mobile-toc__panel" v-if="showMobileTimeline">
          <div class="ce-mobile-toc__title">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="15" y2="18"/></svg>
            {{$t('preview.buttonTOC') || 'Contents'}}
          </div>
          <time-line v-if="timeLine"
                     :chapters="timeLine"
                     :activeYear="activeYear"
                     :activeMonth="activeMonth"
                     :editable="true"
                     :key="timelineKey"
                     @changeMonth="onChangeMonth($event); showMobileTimeline = false"
                     @hideMonth="onHideMonth"
                     @showMonth="onShowMonth"
                     @addContent="smartAddPatch(); showMobileTimeline = false"/>
        </div>
        <div class="ce-mobile-toc__overlay" v-if="showMobileTimeline" @click="showMobileTimeline = false"></div>
        <button class="ce-mobile-toc__fab" @click="showMobileTimeline = !showMobileTimeline">
          <svg v-if="!showMobileTimeline" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="15" y2="18"/></svg>
          <svg v-else viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>
    </div>
    <!-- Sync progress modal -->
    <modal :show="syncModal.visible" class="sync-progress-modal" @close="closeSyncModal">
      <div slot="body" class="sync-progress">
        <div class="sync-progress__icon" :class="'sync-progress__icon--' + syncModal.status">
          <svg v-if="syncModal.status === 'done'" viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          <svg v-else viewBox="0 0 24 24" width="32" height="32" fill="#fff"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
        </div>
        <h3 class="sync-progress__title">
          {{ syncModal.status === 'done' ? $t('editor.syncDone') : $t('editor.syncing') }}
        </h3>
        <p class="sync-progress__desc" v-if="syncModal.status !== 'done'">
          {{ $t('editor.syncingDesc') }}
        </p>
        <p class="sync-progress__desc sync-progress__desc--result" v-else>
          <span v-if="syncModal.addedCount > 0">{{ $t('editor.syncAdded', { count: syncModal.addedCount }) }}</span>
          <span v-else>{{ $t('editor.syncNoNew') }}</span>
        </p>
        <div class="sync-progress__bar" v-if="syncModal.status !== 'done'">
          <div class="sync-progress__fill" :style="{ width: syncModal.progress + '%' }"></div>
        </div>
        <button v-if="syncModal.status === 'done'"
                class="btn primary sync-progress__close"
                @click="closeSyncModal">
          {{ $t('editor.syncClose') }}
        </button>
      </div>
    </modal>
    <!-- Month update modal（接口同 1.0「更新当月内容」flush，完成后自动关闭） -->
    <modal :show="monthUpdateModal.visible" class="sync-progress-modal">
      <div slot="body" class="sync-progress">
        <div class="sync-progress__icon">
          <i class="fa fa-refresh fa-spin" style="font-size: 26px;"></i>
        </div>
        <h3 class="sync-progress__title">
          {{ $t('editor.updatingMonth', { month: monthUpdateModal.label }) }}
        </h3>
      </div>
    </modal>
  </div>
</template>

<script>
import api from 'src/api'
import { findIndex, orderBy } from 'lodash'
import EditorCard from './EditorCard.vue'
import TimeLine from '../Content/Timeline.vue'
import CoverHolder from 'src/components/CoverHolder.vue'
import generateCover from 'src/_utils/generateCover'
import CoverType from '../Cover/CoverType.vue'
import TypesetType from '../Cover/TypesetType.vue'
import InsertPage from '../Cover/InsertPage.vue'
import ColorList from '../Cover/ColorList.vue'
import config from 'src/app.config'

export default {
  name: 'ContentEditor',
  components: { EditorCard, TimeLine, CoverHolder, CoverType, TypesetType, InsertPage, ColorList },
  props: {
    book: { type: Object, required: true },
    design: { type: Object, default: () => ({}) },
    chapters: { type: Array, default: () => [] },
    initialPanel: { type: String, default: 'content' },
    initialYear: { type: String, default: '' },
    initialMonth: { type: String, default: '' }
  },
  data () {
    const d = new Date()
    const month = `0${d.getMonth() + 1}`.substr(-2)
    const day = `0${d.getDate()}`.substr(-2)
    const hours = `0${d.getHours()}`.substr(-2)
    const minutes = `0${d.getMinutes()}`.substr(-2)
    return {
      tab: 'edit',
      editorTimeLine: null,
      items: [],
      deletedItems: [],
      viewMode: 'posts',
      loading: false,
      activeItemId: '',
      batchMode: false,
      selectedItems: new Set(),
      addingAt: -1,
      patchDate: `${d.getFullYear()}-${month}-${day}`,
      patchTime: `${hours}:${minutes}`,
      patchContent: '',
      patchImages: [],
      activeYear: '',
      activeMonth: '',
      currentMonth: null,
      socialChapters: {},
      enabledSources: { facebook: true, patch: true },
      contentChanged: false,
      activePanel: this.initialPanel || 'content',
      designData: null,
      designChanged: false,
      designInited: false,
      thumbKey: Date.now(),
      timelineKey: 0,
      isMobile: window.innerWidth <= 767 || !!window.isMobile,
      showMobileDesign: false,
      showMobileEdit: false,
      showMobileTimeline: false,
      originalCoverImageUrl: '',
      contentSources: [],
      sourcesLoaded: false,
      flashGroupIds: {}, // { [groupId]: true } — 哪张 source card 的数字需要闪烁
      syncModal: {
        visible: false,
        status: 'syncing', // 'syncing' | 'done'
        progress: 0,
        addedCount: 0
      },
      syncProgressTimer: null,
      // 按月更新内容弹窗（接口同 1.0 flush）
      monthUpdateModal: {
        visible: false,
        label: ''
      },
      // === 新版 sources view（按年/月网格）===
      // sourceKey = sourceId + '|' + catType — 同账户多 book 可能挂同 type，必须用 sid 去重
      activeSourceKey: '',
      activeSourceId: '', // 当前选中分类所属 socialbook 的 sid（不一定 = route.query.sid）
      activeCatType: '', // 'facebook' | 'patch' | 'albums' | 'pages'
      monthsGrid: {}, // 当前 active source 的 grid: { '2008': { '08': {total, hidden}, ... }, ... }
      monthsGridLoading: false,
      // 切换 source 时缓存上一个 source 的 grid（含用户本地的 hidden 改动）
      // —— 切回时秒级恢复，不重新拉
      gridCache: {}, // { sourceKey: monthsGrid }
      // 拉 grid 当下的服务端原始 hidden 快照（不被本地 toggle 改），
      // commit 时跟当前 gridCache diff 算出 add/remove
      initialGridCache: {},
      // sourceKey -> bool；左侧绿勾态（"全部月份都 visible"）
      catEnabledMap: {},
      // sourceKey -> bool；左侧"红边框"态（"任意月份 visible"，即本书有这个来源的内容）
      catHasContentMap: {},
      // 该 source 有没有"用户改过但还没排版"的标记（前端态）
      dirtyKeys: {}, // { sourceKey: true }
      applyingChanges: false
    }
  },
  computed: {
    timeLine () {
      return this.editorTimeLine || this.book.timeLine || {}
    },
    coverImage () {
      const d = this.designData || this.design
      if (!d || !d.template) return ''
      return generateCover({ design: d, chapters: this.chapters })
    },
    activeSourceLabel () {
      if (!this.activeSourceKey) return ''
      for (const g of (this.contentSources || [])) {
        for (const c of (g.categories || [])) {
          if (this.categoryToKey(g, c) === this.activeSourceKey) return c.label
        }
      }
      return this.activeSourceKey
    },
    // 右侧 grid 上的"个/张/条"单位 —— albums 是相册（每条 Item 代表一个 album，
    // 而不是一张照片），其他都按帖子算
    activeUnit () {
      if (this.activeCatType === 'albums') return this.$t('editor.albumUnit')
      return this.$t('editor.postUnit')
    },
    hasPendingChanges () {
      const s = this.pendingShow[this.activeSourceKey]
      const h = this.pendingHide[this.activeSourceKey]
      return (s && s.size > 0) || (h && h.size > 0)
    },
    // 必须是 computed —— 之前是 methods 里的 sortedYears()，
    // 模板写 v-for="year in sortedYears" 没加括号，Vue 把 method 当属性，
    // 拿到的是函数对象，遍历空 → 右侧网格空白
    sortedYears () {
      return Object.keys(this.monthsGrid || {}).sort()
    },
    // 右侧网格"全部勾选/全部隐藏"快捷键的态：当前 active source 是否
    // 所有月份都是 visible（任意月份 hidden 就不算）
    allCurrentChecked () {
      const grid = this.monthsGrid || {}
      let any = false
      for (const y of Object.keys(grid)) {
        for (const m of Object.keys(grid[y])) {
          any = true
          if (grid[y][m].hidden) return false
        }
      }
      return any
    },
    coverPreviewUrl () {
      const d = this.designData || this.design
      if (!d || !d.template) return ''
      return generateCover({ design: d, chapters: this.chapters, size: 1920 })
    },
    insertPageImage () {
      const d = this.designData || this.design
      const codeName = (d && d.insertPage) || 'en'
      const year = (new Date()).getFullYear()
      return `https://canvas.xinshu.me/generate/insert-${codeName}?year=${year}&month=1`
    },
    coverImageUrl () {
      if (!this.designData || !this.designData.cover) return ''
      return this.designData.cover.imageUrl || ''
    },
    coverThumbUrl () {
      // thumbKey changes on reset/upload to bust browser cache
      // Fall back to pic (default photo) when imageUrl is cleared, not the full generated cover
      const pic = (this.designData && this.designData.cover && this.designData.cover.pic) || ''
      const base = this.coverImageUrl || pic || this.coverImage
      return base ? (base + (base.includes('?') ? '&' : '?') + '_k=' + this.thumbKey) : ''
    },
    layoutImage () {
      const d = this.designData || {}
      const typeset = d.typeset || this.book.typeset
      if (!typeset) return ''
      const templates = {
        'a5-1': 'https://static.ipastbook.com/assets/images/A5-1-simple.jpg',
        'a5-2': 'https://static.ipastbook.com/assets/images/A5-2-simple.jpg',
        'a4-1': 'https://static.ipastbook.com/assets/images/A4-1-simple.jpg',
        'a4-2': 'https://static.ipastbook.com/assets/images/A4-1-child.jpg'
      }
      return templates[typeset] || ''
    },
    isColorfulCover () {
      return this.designData && config.colorfulCovers.test(this.designData.template)
    },
    currentMonthLabel () {
      if (!this.currentMonth) return ''
      const year = this.activeYear
      const month = parseInt(this.activeMonth, 10)
      const ys = this.$t('preview.yearSuffix')
      const ms = this.$t('preview.monthSuffix')
      if (ys || ms) {
        return `${year}${ys}${month}${ms}`
      }
      // English: "2008/08" format
      return `${year}/${this.activeMonth}`
    },
    canUpdateCurrentMonth () {
      // 只有该月挂了 facebook chapter 才能从上游 flush（补写内容无上游可更新）
      if (!this.currentMonth) return false
      const info = this.socialChapters[this.currentMonth.time]
      return !!(info && info.fb && this.enabledSources.facebook)
    },
    isDesignPanel () {
      return ['cover', 'insert', 'layout'].includes(this.activePanel)
    },
    fbAuthLink () {
      // 给当前用户追加 FB 数据源 —— 走 config 里配置好的 FB OAuth 入口
      const bid = this.$route.query.bid || ''
      const link = config.facebookLoginPath
      const sep = link.includes('?') ? '&' : '?'
      return bid
        ? `${link}${sep}bid=${encodeURIComponent(bid)}&source=posts`
        : link
    },
    insAuthLink () {
      // 同理：给当前用户追加 Instagram 数据源（而非登录新账户）
      const bid = this.$route.query.bid || ''
      const uid = (this.$store && this.$store.getters.user.uid) || ''
      return 'https://ipastbook.com/social/instagram_data/?bid=' + encodeURIComponent(bid) +
        '&uid=' + encodeURIComponent(uid)
    }
  },
  watch: {
    design: {
      handler (val) {
        // Only init once when design prop first gets real data
        if (val && val.template && !this.designInited) {
          this.designInited = true
          this.initDesignData()
        }
      },
      deep: true
    }
  },
  created () {
    // Initialize design data from book prop
    this.initDesignData()
    // 如果直接以 sources panel 入口进来，主动触发一次内容来源加载
    // —— 否则用户要手动切到别的 tab 再切回来才会拉
    if (this.activePanel === 'sources') {
      this.fetchContentSources()
    }
    // Load social book info to get scid mapping (both facebook and patch)
    const { sid } = this.$route.query
    if (sid) {
      this.refreshSocialChapters(sid).then(() => {
        // Try to locate the month from preview position
        let found = false
        if (this.initialYear && this.initialMonth && this.timeLine[this.initialYear]) {
          const target = this.timeLine[this.initialYear].find(
            m => !m.deleted && m.month === this.initialMonth
          )
          if (target) {
            this.activeYear = this.initialYear
            this.activeMonth = target.month
            this.currentMonth = target
            this.loadContent(target)
            found = true
          }
        }
        // Fallback to first visible year/month
        if (!found) {
          const years = Object.keys(this.timeLine).sort()
          for (const year of years) {
            const visible = this.timeLine[year].find(m => !m.deleted)
            if (visible) {
              this.activeYear = year
              this.activeMonth = visible.month
              this.currentMonth = visible
              this.loadContent(visible)
              break
            }
          }
        }
      })
    }
  },
  methods: {
    // Fetch both facebook and patch social chapters
    // Design panel methods
    initDesignData () {
      const d = this.design || {}
      const imgUrl = (d.cover && d.cover.imageUrl) || ''
      const pic = (d.cover && d.cover.pic) || ''
      // Save the original cover image so "Reset to default" can restore it
      this.originalCoverImageUrl = pic || imgUrl
      this.designData = JSON.parse(JSON.stringify({
        title: d.title || '',
        author: d.author || '',
        typeset: d.typeset || this.book.typeset || 'a5-1',
        template: d.template || 'cover-psb-1',
        cover: {
          color: (d.cover && d.cover.color) || '#ffffff',
          imageUrl: imgUrl,
          pic: pic
        },
        insertPage: d.insertPage || 'en'
      }))
    },
    switchPanel (panel) {
      this.activePanel = panel
      if (panel === 'sources') {
        if (!this.sourcesLoaded) {
          this.fetchContentSources()
        } else {
          this._autoSelectFirstSource()
        }
      }
    },
    // 进入 Manage Contents 面板时如果还没选中任何来源，自动选第一个
    _autoSelectFirstSource () {
      if (this.activeSourceKey) return
      for (const g of (this.contentSources || [])) {
        const cat = (g.categories || [])[0]
        if (cat) {
          this.selectCategory(g, cat)
          return
        }
      }
    },
    // 从后端拉最新 user 资料（含 bindInfo.bind.facebook 等），
    // 合并进 Vuex —— 保留 token 不动（后端 fetchUser 不返 token）
    async _refreshCurrentUser () {
      try {
        const user = this.$store && this.$store.getters.user
        if (!user || !user.uid) return
        const resp = await api.fetchUser(user.uid)
        const fresh = (resp && (resp.user || resp)) || {}
        if (!fresh.uid) return
        this.$store.dispatch('setUser', Object.assign({}, user, fresh, {
          token: user.token // 保留 auth token
        }))
      } catch (e) {
        console.warn('refresh user failed:', e && e.message)
      }
    },
    // 返回当前账户下所有 editable 非删 book 的 source_id（sid），
    // 当前 book 的 sid 放第一位。用于 Manage Contents 聚合"账户级别"来源。
    async _collectAccountSids (currentSid) {
      const sids = [currentSid]
      try {
        const uid = (this.$store && this.$store.getters.user && this.$store.getters.user.uid) || ''
        if (!uid) return sids
        const resp = await api.fetchBooks(uid)
        // axios 拦截器已经 camelCase 并 unwrap data，结果是
        // { books: [...], ableToCreateNew: bool }
        const books = (resp && (resp.books || (resp.data && resp.data.books))) || []
        for (const b of books) {
          const s = b.source || b.sourceId
          if (s && s !== currentSid && !sids.includes(s)) sids.push(s)
        }
      } catch (e) {
        console.warn('_collectAccountSids fallback:', e && e.message)
      }
      return sids
    },
    async fetchContentSources () {
      const { sid } = this.$route.query
      if (!sid) {
        this.sourcesLoaded = true
        return
      }
      this.sourcesLoaded = false
      try {
        // 先把 Vuex user.bindInfo 刷新一下 —— X 登录时 setUser 只存了
        // {uid, nickname, avatar, token}，没有 bindInfo.bind.facebook。
        // 否则 FB 卡片头像/名字会退回到 X 用户的（错的）
        await this._refreshCurrentUser()
        // 本账户可能有多本书（不同来源各一本），Manage Contents 要展示
        // **整个账户**已导入的所有来源。先拿当前 book 的，再合并其他 book 的。
        const allSids = await this._collectAccountSids(sid)
        // 对每个 sid 并发拉 fb/patch/albums
        const perSid = await Promise.all(allSids.map(async (s) => {
          const [fb, patch, albums] = await Promise.all([
            api.fetchSocialInfo(s, 'facebook').catch(() => null),
            api.fetchSocialInfo(s, 'patch').catch(() => null),
            api.fetchSocialInfo(s, 'albums').catch(() => null)
          ])
          return { sid: s, fb, patch, albums }
        }))
        // 找出哪个 sid 下 fb/albums/patch 真的有东西，各只取第一个
        const pick = (type) => {
          for (const r of perSid) {
            const d = r[type]
            if (!d || d.errors) continue
            const key = type === 'fb' ? 'facebook' : type
            const arr = (d[key] && (d[key].checked || []).concat(d[key].unchecked || [])) || []
            if (arr.length) return { ...r, pickedType: type }
          }
          return null
        }
        const fbPick = pick('fb')
        const albumsPick = pick('albums')
        const patchPick = pick('patch')
        // 用于下面老代码兼容 —— 取第一个真正有 FB 数据的 sid 当"FB 上下文"
        const fbData = fbPick ? fbPick.fb : null
        const albumsData = (fbPick || albumsPick) ? ((fbPick || albumsPick).albums) : null
        const patchData = patchPick ? patchPick.patch : null
        const sources = []
        // Facebook source group — 只在当前账号真的绑过 FB 且有内容时才显示。
        // 以前无条件 push，会让纯 X 账号也出现一张 "0 posts" 的空 FB 卡片。
        if (fbData && !fbData.errors) {
          const fbChecked = (fbData.facebook && fbData.facebook.checked) || []
          const fbUnchecked = (fbData.facebook && fbData.facebook.unchecked) || []
          // Sum actual post counts using 'selected' or 'total' field per month
          const fbPostCount = fbChecked.concat(fbUnchecked).reduce((sum, m) => sum + (m.selected || m.total || 0), 0)

          // Albums count
          const albumsChecked = (albumsData && albumsData.albums && albumsData.albums.checked) || []
          const albumsUnchecked = (albumsData && albumsData.albums && albumsData.albums.unchecked) || []
          const albumsPostCount = albumsChecked.concat(albumsUnchecked).reduce((sum, m) => sum + (m.selected || m.total || 0), 0)
          const fbPagesCount = (fbData.pages && fbData.pages.length) || 0

          // 没 posts / pages / albums 时，整张 FB 卡片都不展示
          const fbHasAnyContent = fbPostCount > 0 ||
            fbChecked.length > 0 || fbUnchecked.length > 0 ||
            fbPagesCount > 0 ||
            albumsPostCount > 0 ||
            albumsChecked.length > 0 || albumsUnchecked.length > 0
          if (!fbHasAnyContent) {
            // skip Facebook group entirely
          } else {
            const categories = []
            categories.push({
              type: 'facebook',
              label: this.$t('editor.fbPost'),
              count: fbPostCount,
              unit: this.$t('editor.postUnit'),
              checked: fbChecked.length > 0,
              checkedMonths: fbChecked.map(m => m.month),
              uncheckedMonths: fbUnchecked.map(m => m.month)
            })
            // Fan pages — only show if data exists
            if (fbData.pages && fbData.pages.length) {
              categories.push({
                type: 'pages',
                label: this.$t('editor.fbFanPage'),
                count: fbData.pages.length,
                unit: this.$t('editor.postUnit'),
                checked: true,
                checkedMonths: [],
                uncheckedMonths: []
              })
            }
            if (albumsChecked.length + albumsUnchecked.length > 0) {
              categories.push({
                type: 'albums',
                label: this.$t('editor.fbPhotos'),
                // FB Photos 是相册：每个 Item 是一个 album，不是单张照片
                count: albumsChecked.length + albumsUnchecked.length,
                unit: this.$t('editor.albumUnit'),
                checked: albumsChecked.length > 0,
                checkedMonths: albumsChecked.map(m => m.month || m.albumId),
                uncheckedMonths: albumsUnchecked.map(m => m.month || m.albumId)
              })
            }
            const user = this.$store ? this.$store.getters.user : {}
            // 优先读 bind_info.bind.facebook (X 用户挂 FB 的情况)
            // 再读 bind_info.source.facebook (FB 为主 source 的老情况)
            // 最后退回到当前用户的 Vuex user —— 但注意这个可能是别的 source
            // (比如 X)，头像/名字会错，所以尽量不走到这里
            const bind = (user.bindInfo && user.bindInfo.bind) || {}
            const source = (user.bindInfo && user.bindInfo.source) || {}
            const fbInfo = bind.facebook || source.facebook || {}
            // Compute date range from FB months
            const allFbMonths = fbChecked.concat(fbUnchecked).map(m => m.month).sort()
            const fbDateRange = this.formatDateRange(allFbMonths)
            sources.push({
              id: 'facebook',
              sourceId: fbPick.sid,
              icon: 'fa fa-facebook',
              iconType: 'fb',
              avatar: fbInfo.avatar || '',
              name: fbInfo.username || fbInfo.nickname || 'Facebook',
              dateRange: fbDateRange,
              actionLabel: this.$t('editor.syncData'),
              actionType: 'sync',
              categories
            })
          } // end else (fbHasAnyContent)
        }
        // Patch (manual) source group — 跨 book 聚合后，Twitter 书的 patch（X 推文）
        // 也会被挑出来作为"该账户的 X 来源"展示。
        // 0 条且没月份 → 整张卡不显示（用户要求：空补写不展示）
        if (patchData && !patchData.errors) {
          const patchChecked = (patchData.patch && patchData.patch.checked) || []
          const patchUnchecked = (patchData.patch && patchData.patch.unchecked) || []
          const patchPostCount = patchChecked.concat(patchUnchecked).reduce((sum, m) => sum + (m.selected || m.total || 0), 0)
          const patchHasAny = patchPostCount > 0 || patchChecked.length > 0 || patchUnchecked.length > 0
          if (!patchHasAny) {
            // skip entirely
            this.contentSources = sources
            this.sourcesLoaded = true
            this._refreshCatEnabledMap()
            this._autoSelectFirstSource()
            return
          }
          const allPatchMonths = patchChecked.concat(patchUnchecked).map(m => m.month).sort()
          const patchDateRange = this.formatDateRange(allPatchMonths)
          sources.push({
            id: 'patch',
            sourceId: patchPick.sid,
            icon: '',
            iconText: '',
            iconType: 'patch',
            iconImage: require('../../../../static/images/logo/logo-transparent.svg'),
            avatar: null,
            name: 'iPastbook ' + this.$t('editor.patchContent'),
            dateRange: patchDateRange,
            actionLabel: this.$t('editor.continueWrite'),
            actionType: 'patch',
            categories: [{
              type: 'patch',
              label: 'iPastbook ' + this.$t('editor.patchContent'),
              count: patchPostCount,
              unit: this.$t('editor.postUnit'),
              checked: patchChecked.length > 0,
              checkedMonths: patchChecked.map(m => m.month),
              uncheckedMonths: patchUnchecked.map(m => m.month)
            }]
          })
        }
        this.contentSources = sources
      } catch (e) {
        console.error('fetchContentSources error:', e)
      }
      this.sourcesLoaded = true
      this._refreshCatEnabledMap()
      this._autoSelectFirstSource()
    },
    formatDateRange (months) {
      if (!months.length) return ''
      const first = months[0]
      const last = months[months.length - 1]
      const fmtMonth = (m) => {
        const y = m.substr(0, 4)
        const mo = parseInt(m.substr(4, 2), 10)
        const ys = this.$t('preview.yearSuffix')
        const ms = this.$t('preview.monthSuffix')
        if (ys || ms) return `${y}${ys}${mo}${ms}`
        return `${y}/${m.substr(4, 2)}`
      }
      if (first === last) return fmtMonth(first)
      return `${fmtMonth(first)} ~ ${fmtMonth(last)}`
    },
    handleSourceAction (group) {
      if (group.actionType === 'sync') {
        this.startFbSync()
      } else if (group.actionType === 'patch') {
        // Switch to content panel and navigate to patch entry
        this.activePanel = 'content'
      }
    },
    // ==================== 新版 sources view 方法 ====================
    // key = sourceId|catType — 一个账户里同 type 可能挂在不同 sid 上（多 book），
    // 必须把 sid 一起编码，否则 activeSourceKey 撞车 + loadMonthsGrid 拿错 sid
    categoryToKey (group, cat) {
      return (group.sourceId || '') + '|' + cat.type
    },
    selectCategory (group, cat) {
      const key = this.categoryToKey(group, cat)
      this.activeSourceKey = key
      this.activeSourceId = group.sourceId || ''
      this.activeCatType = cat.type
      // 缓存上一份 grid 到 gridCache（保留本地未提交的勾选状态）
      // —— 切回这个 source 时直接恢复，不重拉
      if (this._lastActiveKey && this._lastActiveKey !== key) {
        this.$set(this.gridCache, this._lastActiveKey, this.monthsGrid)
      }
      this._lastActiveKey = key
      if (this.gridCache[key]) {
        this.monthsGrid = this.gridCache[key]
      } else {
        this.loadMonthsGrid()
      }
    },
    // 绿勾视觉态 = 该 source 所有月份都 visible（任何月份 hidden → 父级不勾）。
    // 优先看 gridCache（用户本地改动直接反映），没拉过的 source 走 catEnabledMap，
    // 都没有再退回 fetchSocialInfo 给的 checkedMonths 粗略判断
    catEnabled (group, cat) {
      const key = this.categoryToKey(group, cat)
      const grid = this.gridCache[key]
      if (grid && Object.keys(grid).length) {
        let hasMonths = false
        let allVisible = true
        for (const y of Object.keys(grid)) {
          for (const m of Object.keys(grid[y])) {
            hasMonths = true
            if (grid[y][m].hidden) allVisible = false
          }
        }
        return hasMonths && allVisible
      }
      if (Object.prototype.hasOwnProperty.call(this.catEnabledMap, key)) {
        return this.catEnabledMap[key]
      }
      const checked = (cat.checkedMonths && cat.checkedMonths.length) || 0
      const unchecked = (cat.uncheckedMonths && cat.uncheckedMonths.length) || 0
      return checked > 0 && unchecked === 0
    },
    // 拉 months_grid 算 enabled，写到 catEnabledMap（contentSources 加载后并发跑）
    async _refreshCatEnabledMap () {
      const tasks = []
      for (const g of (this.contentSources || [])) {
        for (const c of (g.categories || [])) {
          tasks.push(this._loadCatEnabled(g, c))
        }
      }
      try { await Promise.all(tasks) } catch (e) {}
    },
    async _loadCatEnabled (group, cat) {
      if (!group.sourceId || !cat.type) return
      try {
        const grid = await api.fetchMonthsGrid(group.sourceId, cat.type)
        const years = (grid && grid.years) || {}
        let hasMonths = false
        let allVisible = true
        let anyVisible = false
        for (const y of Object.keys(years)) {
          for (const m of Object.keys(years[y])) {
            hasMonths = true
            if (years[y][m].hidden) allVisible = false
            else anyVisible = true
          }
        }
        const key = this.categoryToKey(group, cat)
        this.$set(this.catEnabledMap, key, hasMonths && allVisible)
        this.$set(this.catHasContentMap, key, hasMonths && anyVisible)
      } catch (e) {}
    },
    // 红边框 = "本书有这个来源的内容"，即任意月份未被隐藏。
    // 优先用 gridCache 实时算（用户本地操作立刻反映）；
    // 否则用 catHasContentMap 快照；最后退回粗略 fallback。
    catHasContent (group, cat) {
      const key = this.categoryToKey(group, cat)
      const grid = this.gridCache[key]
      if (grid && Object.keys(grid).length) {
        for (const y of Object.keys(grid)) {
          for (const m of Object.keys(grid[y])) {
            if (!grid[y][m].hidden) return true
          }
        }
        return false
      }
      if (Object.prototype.hasOwnProperty.call(this.catHasContentMap, key)) {
        return this.catHasContentMap[key]
      }
      return !!(cat.checkedMonths && cat.checkedMonths.length > 0)
    },
    // 点绿勾 = 一键切整个 source on/off，纯前端。
    // 如果该 source 的 grid 还没拉过，先 fetch 一次拿月份列表。
    async toggleCategoryAll (group, cat) {
      const sid = group.sourceId
      const type = cat.type
      if (!sid || !type) return
      const key = this.categoryToKey(group, cat)
      // 没拉过 grid → 先拉一次（不会改动数据，只是为了拿月份）
      if (!this.gridCache[key]) {
        try {
          const res = await api.fetchMonthsGrid(sid, type)
          const years = (res && res.years) || {}
          this.$set(this.gridCache, key, years)
          this.$set(this.initialGridCache, key, JSON.parse(JSON.stringify(years)))
        } catch (e) { console.error('preload grid', e); return }
      }
      const grid = this.gridCache[key]
      if (!grid || !Object.keys(grid).length) return
      const wasEnabled = this.catEnabled(group, cat)
      const target = !wasEnabled
      // 翻所有月份的 hidden
      for (const y of Object.keys(grid)) {
        for (const m of Object.keys(grid[y])) {
          this.$set(grid[y][m], 'hidden', !target)
        }
      }
      this.$set(this.dirtyKeys, key, true)
      if (this.activeSourceKey === key) {
        this.monthsGrid = grid
        this.$forceUpdate()
      }
      this._rebuildTimeline()
      this._scheduleCommit()
    },
    async loadMonthsGrid () {
      const sid = this.activeSourceId
      const type = this.activeCatType
      const key = this.activeSourceKey
      if (!sid || !type) return
      this.monthsGridLoading = true
      this.monthsGrid = {}
      try {
        const res = await api.fetchMonthsGrid(sid, type)
        this.monthsGrid = (res && res.years) || {}
        // 拷一份原始 hidden 状态，commit 时对比算 add/remove
        this.$set(this.gridCache, key, this.monthsGrid)
        this.$set(this.initialGridCache, key, JSON.parse(JSON.stringify(this.monthsGrid)))
      } catch (e) {
        console.error('loadMonthsGrid', e)
      }
      this.monthsGridLoading = false
    },
    // 渲染态完全看 monthsGrid[y][m].hidden（toggle 直接改这里）
    monthEffectiveChecked (year, month) {
      const cell = (this.monthsGrid[year] || {})[month]
      return !!(cell && !cell.hidden)
    },
    // 用户点月份 chip：本地 flip + 同步 editorTimeLine + 防抖 commit
    toggleMonth (year, month) {
      const cell = (this.monthsGrid[year] || {})[month]
      if (!cell) return
      this.$set(cell, 'hidden', !cell.hidden)
      this.$set(this.dirtyKeys, this.activeSourceKey, true)
      this._rebuildTimeline()
      this._scheduleCommit()
    },
    // 防抖：连续 toggle 600ms 内只攒一次 commit
    _scheduleCommit () {
      if (this._commitTimer) clearTimeout(this._commitTimer)
      this._commitTimer = setTimeout(() => {
        this._commitTimer = null
        const dirty = Object.keys(this.dirtyKeys).filter(k => this.dirtyKeys[k])
        if (dirty.length) {
          this._commitAllDirty(dirty).then(() => {
          // commit 完成后触发 typeset；preview 用得上
            this.triggerRetypeset()
          })
        }
      }, 600)
    },
    // 从 gridCache + book.currentChapters 重建 editorTimeLine
    // —— Edit Content 时间轴跟 Manage Contents 共用同一份事实
    _rebuildTimeline () {
      const monthVisible = {}
      for (const key of Object.keys(this.gridCache)) {
        const grid = this.gridCache[key] || {}
        for (const y of Object.keys(grid)) {
          for (const m of Object.keys(grid[y])) {
            const t = y + m
            if (!(t in monthVisible)) monthVisible[t] = false
            if (!grid[y][m].hidden) monthVisible[t] = true
          }
        }
      }
      const pageMap = {}
      ;((this.book && this.book.currentChapters) || []).forEach(ch => {
        const t = ch.time || ch.month
        if (t) pageMap[t] = ch.totalPage || ch.total_page || 0
      })
      const out = {}
      for (const t of Object.keys(monthVisible)) {
        if (t.length !== 6) continue
        const y = t.substr(0, 4)
        const m = t.substr(4, 2)
        if (!out[y]) out[y] = []
        out[y].push({
          month: m,
          time: t,
          deleted: !monthVisible[t],
          pages: pageMap[t] || 0
        })
      }
      Object.keys(out).forEach(y => {
        out[y].sort((a, b) => a.time.localeCompare(b.time))
      })
      this.editorTimeLine = out
    },
    sortedMonths (year) {
      return Object.keys(this.monthsGrid[year] || {}).sort()
    },
    yearTotal (year) {
      return Object.values(this.monthsGrid[year] || {}).reduce((s, c) => s + (c.total || 0), 0)
    },
    yearChecked (year) {
      const months = this.sortedMonths(year)
      return months.length > 0 && months.every(m => this.monthEffectiveChecked(year, m))
    },
    yearIndeterminate (year) {
      const months = this.sortedMonths(year)
      const checked = months.filter(m => this.monthEffectiveChecked(year, m)).length
      return checked > 0 && checked < months.length
    },
    toggleYear (year) {
      const target = !this.yearChecked(year)
      this.sortedMonths(year).forEach(m => {
        const cell = (this.monthsGrid[year] || {})[m]
        if (!cell) return
        this.$set(cell, 'hidden', !target)
      })
      this.$set(this.dirtyKeys, this.activeSourceKey, true)
      this._rebuildTimeline()
      this._scheduleCommit()
    },
    // 右上角快捷：勾选/隐藏当前 source 的全部年/月
    toggleAllCurrent () {
      if (!this.activeSourceKey) return
      const grid = this.monthsGrid || {}
      const keys = Object.keys(grid)
      if (!keys.length) return
      const target = !this.allCurrentChecked
      for (const y of keys) {
        for (const m of Object.keys(grid[y])) {
          this.$set(grid[y][m], 'hidden', !target)
        }
      }
      this.$set(this.dirtyKeys, this.activeSourceKey, true)
      this._rebuildTimeline()
      this._scheduleCommit()
    },
    // 月份在 chip 上的显示：CJK = "1月"，英文 = "Jan."
    // 用 preview.monthSuffix 是否为空来判断是不是 CJK 语言
    monthDisplay (m) {
      const n = parseInt(m, 10)
      const suffix = this.$t('preview.monthSuffix')
      if (suffix) return n + suffix
      const en = ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'Jun.',
        'Jul.', 'Aug.', 'Sep.', 'Oct.', 'Nov.', 'Dec.']
      return en[n - 1] || String(n)
    },
    // Save & Preview 顶部按钮：toggle 已经走防抖自动 commit 了，这里
    // 只需把可能在防抖窗口里没飞出去的最后一批 dirty 立刻 flush，再
    // 抛 save-preview 让父组件 triggerTypeset + 进 preview。
    async onSavePreview () {
      if (this._commitTimer) {
        clearTimeout(this._commitTimer)
        this._commitTimer = null
      }
      const dirty = Object.keys(this.dirtyKeys).filter(k => this.dirtyKeys[k])
      if (dirty.length) {
        await this._commitAllDirty(dirty)
      }
      this.$emit('save-preview', this.contentChanged || this.designChanged)
    },
    // 对每个 dirty source：拿 gridCache（或当前 monthsGrid）+ initialGridCache
    // 算出 add/remove，然后 updateSocialBook(sid, {type: {add, remove}})。
    async _commitAllDirty (keys) {
      this.applyingChanges = true
      try {
        // 把当前 active source 的最新 monthsGrid 也存回 cache
        if (this.activeSourceKey) {
          this.$set(this.gridCache, this.activeSourceKey, this.monthsGrid)
        }
        const tasks = []
        for (const key of keys) {
          const grid = this.gridCache[key]
          const initial = this.initialGridCache[key]
          if (!grid || !initial) continue
          const idx = key.indexOf('|')
          if (idx < 0) continue
          const sid = key.slice(0, idx)
          const type = key.slice(idx + 1)
          if (!sid || !type) continue
          const add = []
          const remove = []
          for (const y of Object.keys(grid)) {
            for (const m of Object.keys(grid[y])) {
              const ym = y + m
              const nowHidden = !!grid[y][m].hidden
              const wasHidden = !!((initial[y] || {})[m] || {}).hidden
              if (nowHidden === wasHidden) continue
              if (nowHidden) remove.push(ym)
              else add.push(ym)
            }
          }
          if (!add.length && !remove.length) continue
          // 走 apply_months —— 它对所有 source 类型（含 albums = fa_*/pages = fp_*）
          // 都按 month 范围查 Item 表正确处理。老 updateSocialBook 对 albums 期望
          // album_id（不是 month），前端只有 month 拿不到 album_id，会 silently no-op
          tasks.push(api.applyMonths(sid, {
            type: type,
            show: add,
            hide: remove
          }).then(() => {
            this.$set(this.dirtyKeys, key, false)
            this.$set(this.initialGridCache, key, JSON.parse(JSON.stringify(grid)))
          }).catch(e => { console.error('applyMonths commit', key, e) }))
        }
        await Promise.all(tasks)
        this.contentChanged = true
        // 不在这里手动 triggerTypeset —— Previewer.vue 的 saveAndReload
        // 已经会在 save-preview 事件处理里调，避免重复
        this._refreshCatEnabledMap()
      } finally {
        this.applyingChanges = false
      }
    },
    getFbPostCount () {
      const fb = this.contentSources.find(s => s.id === 'facebook')
      if (!fb || !fb.categories) return 0
      const cat = fb.categories.find(c => c.type === 'facebook')
      return (cat && cat.count) || 0
    },
    async startFbSync () {
      const beforeCount = this.getFbPostCount()
      this.syncModal = {
        visible: true,
        status: 'syncing',
        progress: 6,
        addedCount: 0
      }
      this.startSyncProgressAnim()
      try {
        const bid = (this.$route.query && this.$route.query.bid) || ''
        const res = await api.triggerFbSync(bid)
        if (!res || !res.ok) {
          // token 失效或未授权：跳 OAuth
          if (res && res.oauthUrl) {
            this.stopSyncProgressAnim()
            this.syncModal.visible = false
            window.location.href = res.oauthUrl
            return
          }
          throw new Error((res && res.error) || 'sync_failed')
        }
        // 轮询同步状态
        await this.pollFbSyncStatus(res.sid)
        // 拉新后的内容
        this.sourcesLoaded = false
        await this.fetchContentSources()
      } catch (e) {
        console.error('startFbSync error', e)
      }
      this.stopSyncProgressAnim()
      this.syncModal.progress = 100
      const after = this.getFbPostCount()
      this.syncModal.addedCount = Math.max(0, after - beforeCount)
      setTimeout(() => {
        if (this.syncModal.visible) {
          this.syncModal.status = 'done'
        }
      }, 300)
    },
    startSyncProgressAnim () {
      if (this.syncProgressTimer) clearInterval(this.syncProgressTimer)
      this.syncProgressTimer = setInterval(() => {
        if (this.syncModal.progress < 90) {
          const inc = Math.random() * 5 + 1.5
          this.syncModal.progress = Math.min(90, this.syncModal.progress + inc)
        }
      }, 600)
    },
    stopSyncProgressAnim () {
      if (this.syncProgressTimer) {
        clearInterval(this.syncProgressTimer)
        this.syncProgressTimer = null
      }
    },
    async pollFbSyncStatus (sid, maxWaitMs, intervalMs) {
      maxWaitMs = maxWaitMs || 90000
      intervalMs = intervalMs || 2500
      const start = Date.now()
      while (Date.now() - start < maxWaitMs) {
        await new Promise(resolve => setTimeout(resolve, intervalMs))
        try {
          const s = await api.fetchFbSyncStatus(sid)
          if (s && s.ok && !s.postsGetting) return
        } catch (e) {
          // 容错继续轮询
        }
      }
    },
    closeSyncModal () {
      this.syncModal.visible = false
      this.stopSyncProgressAnim()
    },
    async toggleSourceCategory (group, cat) {
      cat.checked = !cat.checked
      const { sid } = this.$route.query
      if (!sid) return
      // When checked: add all months; when unchecked: remove all months
      const allMonths = cat.checkedMonths.concat(cat.uncheckedMonths)
      if (!allMonths.length) return
      const payload = {}
      if (cat.checked) {
        payload[cat.type] = { add: allMonths }
      } else {
        payload[cat.type] = { remove: allMonths }
      }
      try {
        this.$toast({ message: this.$t('content.toastHandling'), position: 'bottom' })
        await api.updateSocialBook(sid, payload)
        this.contentChanged = true
        // Trigger retypeset and wait for it to complete
        this.triggerRetypeset()
        // Refresh enabledSources and scid mapping
        await this.refreshSocialChapters(sid)
        // Reload book data from server to get updated timeLine
        const bid = this.$route.query.bid
        if (bid) {
          const bookData = await api.fetchBook(bid)
          if (bookData && bookData.timeLine) {
            this.$set(this.book, 'timeLine', bookData.timeLine)
            this.$set(this.book, 'currentChapters', bookData.currentChapters || [])
            this.editorTimeLine = JSON.parse(JSON.stringify(bookData.timeLine))
          }
        }
        this.timelineKey++
        // Reload current month content if viewing edit panel
        if (this.currentMonth) {
          this.loadContent(this.currentMonth)
        }
        this.$toast({ message: this.$t('content.toastSaveDone'), position: 'bottom' })
      } catch (e) {
        // Revert on error
        cat.checked = !cat.checked
        this.$toast({ message: this.$t('content.toastUpdateFailed'), position: 'bottom' })
      }
    },
    resetCoverImage () {
      // Restore the original cover image (pic or the initial imageUrl), not just clear it
      this.$set(this.designData.cover, 'imageUrl', this.originalCoverImageUrl || '')
      this.thumbKey = Date.now()
      this.saveDesign()
    },
    async onUploadCover (files) {
      if (!files || !files.length) return
      const file = files[0].source || files[0]
      const formData = new FormData()
      formData.append('image', file)
      try {
        const data = await api.uploadImage(formData)
        if (!data || data.errors) {
          this.$toast({ message: this.$t('cover.uploadError') })
          return
        }
        let { origin, hostname } = window.location
        if (hostname === 'localhost' || /\d/g.test(origin)) origin = 'https://ipastbook.com'
        data.image = /http?s:/.test(data.image) ? data.image : `${origin}${data.image}`
        this.designData.cover.imageUrl = data.image
        this.thumbKey = Date.now()
        this.saveDesign()
      } catch (e) {
        this.$toast({ message: this.$t('cover.uploadError') })
      }
    },
    saveDesign () {
      const bid = this.$route.query.bid
      if (!bid || !this.designData) return
      this.designChanged = true
      // Merge designData into the full design object (API requires all fields)
      const fullDesign = Object.assign({}, this.design, this.designData)
      // Deep merge cover object — preserve pic and other fields from original design
      fullDesign.cover = Object.assign({}, this.design.cover, this.designData.cover)
      // Send empty imageUrl so the server clears the custom image on reset
      if (!fullDesign.cover.imageUrl) {
        fullDesign.cover.imageUrl = ''
      }
      // Sync back to book.design so reopening editor has latest data
      if (this.book.design) {
        Object.assign(this.book.design, JSON.parse(JSON.stringify(fullDesign)))
      }
      api.updateCoverDesign(bid, fullDesign).then((data) => {
        if (data && data.errors) {
          console.error('saveDesign error:', data.errors)
          return
        }
        this.$toast({ message: this.$t('content.toastSaveDone'), position: 'bottom' })
      })
    },
    onChangeCover () {
      this.saveDesign()
    },
    onChangeColor (color) {
      this.designData.cover.color = color
      this.saveDesign()
    },
    onChangeTypeset (name) {
      this.designData.typeset = name
      if (/a4-2$/i.test(name)) {
        this.designData.insertPage = 'child'
      }
      this.saveDesign()
    },
    onChangeInsert (insert) {
      this.designData.insertPage = insert
      this.saveDesign()
    },
    // socialChapters[month] = { fb: scid, patch: scid } — maps months to source scids
    // Builds scid mapping and enabledSources state.
    // Also adds unchecked months from ENABLED sources to timeline as hidden (deleted: true),
    // so they appear in "Hidden months" and can be shown again.
    // Does NOT add months from DISABLED sources.
    async refreshSocialChapters (sid) {
      // 拉所有 source 的 months_grid 到 gridCache + initialGridCache —— 这是
      // Edit Content 时间轴和 Manage Contents 网格共用的事实源。
      // 拉完 _rebuildTimeline 把 editorTimeLine 算出来。
      this.editorTimeLine = {}
      this.socialChapters = {}
      this.enabledSources = { facebook: false, patch: false }
      // 拉 socialChapters 老接口（loadContent / patch 提交还在用 scid）
      const [fbData, patchData] = await Promise.all([
        api.fetchSocialInfo(sid, 'facebook').catch(() => null),
        api.fetchSocialInfo(sid, 'patch').catch(() => null)
      ])
      if (fbData && fbData.facebook) {
        const checked = fbData.facebook.checked || []
        const unchecked = fbData.facebook.unchecked || []
        this.enabledSources.facebook = checked.length > 0
        checked.concat(unchecked).forEach(item => {
          if (!this.socialChapters[item.month]) this.socialChapters[item.month] = {}
          this.socialChapters[item.month].fb = item.scid
        })
      }
      if (patchData && patchData.patch) {
        const patchChecked = patchData.patch.checked || []
        const patchUnchecked = patchData.patch.unchecked || []
        this.enabledSources.patch = patchChecked.length > 0
        patchChecked.concat(patchUnchecked).forEach(item => {
          if (!this.socialChapters[item.month]) this.socialChapters[item.month] = {}
          this.socialChapters[item.month].patch = item.scid
        })
      }
      // 跨所有 source 类型拉 months_grid 到 gridCache
      const sourceTypes = ['facebook', 'patch', 'albums', 'pages']
      const sources = (this.contentSources || [])
      const sidByType = {}
      sources.forEach(g => {
        (g.categories || []).forEach(c => {
          if (sourceTypes.indexOf(c.type) !== -1 && g.sourceId) {
            sidByType[c.type] = g.sourceId
          }
        })
      })
      // contentSources 还没加载时，retry 一下当前 sid（fallback）
      const tasks = sourceTypes.map(t => {
        const targetSid = sidByType[t] || sid
        return api.fetchMonthsGrid(targetSid, t).catch(() => null).then(grid => {
          if (!grid || !grid.years) return
          const key = targetSid + '|' + t
          this.$set(this.gridCache, key, grid.years)
          this.$set(this.initialGridCache, key, JSON.parse(JSON.stringify(grid.years)))
        })
      })
      await Promise.all(tasks)
      this._rebuildTimeline()
    },
    // Ensure the target month exists in the editor's timeLine
    ensureMonthInTimeline (year, month, time) {
      const tl = this.editorTimeLine
      if (!tl) return
      if (!tl[year]) {
        this.$set(tl, year, [])
      }
      const exists = tl[year].some(m => m.time === time)
      if (!exists) {
        tl[year].push({ month, time, deleted: false, pages: 0 })
        tl[year].sort((a, b) => a.time.localeCompare(b.time))
      }
    },
    onChangeMonth (month) {
      this.activeYear = month.time.substr(0, 4)
      this.activeMonth = month.month
      this.currentMonth = month
      this.cancelBatch()
      this.cancelAdding()
      this.activeItemId = ''
      this.loadContent(month)
    },
    async loadContent (month) {
      const { sid } = this.$route.query
      if (!sid) return
      const chapterInfo = this.socialChapters[month.time]
      if (!chapterInfo) {
        this.items = []
        this.loading = false
        return
      }
      this.loading = true
      this.items = []
      this.deletedItems = []
      this.viewMode = 'posts'
      // Fetch only from enabled sources (checked in Manage Contents)
      const sources = []
      if (chapterInfo.fb && this.enabledSources.facebook) sources.push({ scid: chapterInfo.fb, type: 'facebook' })
      if (chapterInfo.patch && this.enabledSources.patch) sources.push({ scid: chapterInfo.patch, type: 'patch' })
      if (!sources.length) {
        this.items = []
        this.deletedItems = []
        this.loading = false
        return
      }
      try {
        const results = await Promise.all(
          sources.map(s => api.fetchSocialChapter(sid, s.scid, 1, 100).catch(() => null))
        )
        let allItems = []
        let allDeleted = []
        results.forEach((data, i) => {
          if (data && data.currentItems) {
            data.currentItems.forEach(item => { item._source = sources[i].type })
            allItems = allItems.concat(data.currentItems)
          }
          if (data && data.deletedItems) {
            data.deletedItems.forEach(item => { item._source = sources[i].type })
            allDeleted = allDeleted.concat(data.deletedItems)
          }
        })
        this.items = orderBy(allItems, ['postDate'], ['asc'])
        this.deletedItems = orderBy(allDeleted, ['postDate'], ['asc'])
      } catch (e) {
        // ignore
      }
      this.loading = false
      // 滚动到第一条内容
      this.$nextTick(() => {
        const list = this.$refs.contentList
        if (list) {
          const firstCard = list.querySelector('.editor-card')
          if (firstCard) {
            firstCard.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        }
      })
    },
    getScidForItem (item) {
      if (!this.currentMonth) return null
      const info = this.socialChapters[this.currentMonth.time]
      if (!info) return null
      // Use the scid matching the item's source type
      if (item && item._source === 'patch') return info.patch || info.fb
      return info.fb || info.patch
    },
    getCurrentScid () {
      if (!this.currentMonth) return null
      const info = this.socialChapters[this.currentMonth.time]
      return info ? (info.fb || info.patch) : null
    },
    hideEmptyMonth () {
      if (this.items.length > 0 || !this.currentMonth) return
      // Mark current month as deleted in timeLine so Timeline hides it
      const tl = this.editorTimeLine || this.book.timeLine
      if (!tl) return
      const year = this.activeYear
      const months = tl[year]
      if (!months) return
      const m = months.find(m => m.time === this.currentMonth.time)
      if (m) {
        this.$set(m, 'deleted', true)
        this.timelineKey++
      }
    },
    triggerRetypeset () {
      const bid = this.$route.query.bid || this.$route.params.bid
      if (bid) api.triggerTypeset(bid)
    },
    // Build API payload grouped by source type (facebook/patch)
    // A month may belong to multiple enabled sources — include ALL of them
    buildSourcePayload (times, action) {
      const payload = {}
      times.forEach(time => {
        const info = this.socialChapters[time]
        if (!info) return
        if (info.fb && this.enabledSources.facebook) {
          if (!payload.facebook) payload.facebook = { [action]: [] }
          payload.facebook[action].push(time)
        }
        if (info.patch && this.enabledSources.patch) {
          if (!payload.patch) payload.patch = { [action]: [] }
          payload.patch[action].push(time)
        }
        // Fallback: if no enabled source matched, try any available source
        if (!Object.keys(payload).length) {
          const source = info.fb ? 'facebook' : 'patch'
          if (!payload[source]) payload[source] = { [action]: [] }
          payload[source][action].push(time)
        }
      })
      return payload
    },
    async updateMonthVisibility (times, action) {
      // Edit Content 的 hide / show 月份按钮 = Manage Contents 勾选月份。
      // 走同一条后端 apply_months —— ImportService.update_book_with_items。
      // 对每个 source 类型并发发请求，后端根据 month 范围查 Item 表，
      // 没匹配的 source 自动跳过；这样 facebook/patch/albums/pages 全覆盖。
      const { sid } = this.$route.query
      if (!sid || !times.length) return
      const sourceTypes = ['facebook', 'patch', 'albums', 'pages']
      const payloadKey = action === 'add' ? 'show' : 'hide'
      const tasks = sourceTypes.map(t =>
        api.applyMonths(sid, { type: t, [payloadKey]: times })
          .catch(e => { console.warn('apply_months', t, e && e.message) })
      )
      await Promise.all(tasks)
      this.contentChanged = true
      this.triggerRetypeset()
    },
    hideCurrentMonth () {
      if (!this.currentMonth) return
      this.onHideMonth(this.currentMonth)
    },
    // 更新当月内容：flush 上游数据（接口同 1.0），弹窗提示，完成后自动关闭并重载本月
    async updateCurrentMonth () {
      const { sid } = this.$route.query
      const month = this.currentMonth
      const info = month && this.socialChapters[month.time]
      const scid = info && info.fb
      if (!sid || !scid || this.monthUpdateModal.visible) return
      this.monthUpdateModal = { visible: true, label: this.currentMonthLabel }
      try {
        const data = await api.updateItems(sid, scid, 'facebook')
        if (!data || data.errors) throw new Error('flush_failed')
        await this.loadContent(month)
        this.contentChanged = true
        this.$toast({ message: this.$t('content.toastUpdateDone'), position: 'bottom' })
      } catch (e) {
        this.$toast({ message: this.$t('content.toastUpdateFailed'), position: 'bottom' })
      }
      this.monthUpdateModal.visible = false
    },
    onHideMonth (month) {
      // Cache deleted items for this month before hiding
      // so we can re-apply deletions if user shows it again
      if (this.currentMonth && this.currentMonth.time === month.time && this.deletedItems.length) {
        this.$set(this.hiddenMonthDeletedItems, month.time,
          this.deletedItems.map(item => ({ itemId: item.itemId, _source: item._source })))
      }
      this.updateMonthVisibility([month.time], 'remove').then(() => {
        this.$set(month, 'deleted', true)
        this.timelineKey++
        if (this.currentMonth && this.currentMonth.time === month.time) {
          this.switchToNextVisibleMonth()
        }
        this.$toast({ message: this.$t('content.toastSaveDone'), position: 'bottom' })
      })
    },
    onShowMonth (month) {
      this.updateMonthVisibility([month.time], 'add').then(() => {
        this.$set(month, 'deleted', false)
        this.timelineKey++
        const year = month.time.substr(0, 4)
        this.activeYear = year
        this.activeMonth = month.month
        this.currentMonth = month
        this.loadContent(month)
        this.$toast({ message: this.$t('content.toastSaveDone'), position: 'bottom' })
      })
    },
    onHideYear (year) {
      const tl = this.editorTimeLine || this.book.timeLine
      if (!tl || !tl[year]) return
      const visibleMonths = tl[year].filter(m => !m.deleted)
      if (!visibleMonths.length) return
      const times = visibleMonths.map(m => m.time)
      this.updateMonthVisibility(times, 'remove').then(() => {
        visibleMonths.forEach(m => this.$set(m, 'deleted', true))
        this.timelineKey++
        if (this.currentMonth && this.activeYear === year) {
          this.switchToNextVisibleMonth()
        }
        this.$toast({ message: this.$t('content.toastSaveDone'), position: 'bottom' })
      })
    },
    onShowYear (year) {
      const tl = this.editorTimeLine || this.book.timeLine
      if (!tl || !tl[year]) return
      const hiddenMonths = tl[year].filter(m => m.deleted)
      if (!hiddenMonths.length) return
      const times = hiddenMonths.map(m => m.time)
      this.updateMonthVisibility(times, 'add').then(() => {
        hiddenMonths.forEach(m => this.$set(m, 'deleted', false))
        this.timelineKey++
        const first = hiddenMonths[0]
        this.activeYear = year
        this.activeMonth = first.month
        this.currentMonth = first
        this.loadContent(first)
        this.$toast({ message: this.$t('content.toastSaveDone'), position: 'bottom' })
      })
    },
    switchToNextVisibleMonth () {
      const tl = this.editorTimeLine || this.book.timeLine
      if (!tl) return
      const curTime = this.currentMonth ? this.currentMonth.time : ''
      // Flatten all months sorted by time, find next after current
      const allMonths = []
      for (const year of Object.keys(tl).sort()) {
        tl[year].forEach(m => allMonths.push({ ...m, year }))
      }
      allMonths.sort((a, b) => a.time.localeCompare(b.time))
      const curIdx = allMonths.findIndex(m => m.time === curTime)
      // Search forward from current position
      for (let i = curIdx + 1; i < allMonths.length; i++) {
        if (!allMonths[i].deleted) {
          const next = allMonths[i]
          this.activeYear = next.year
          this.activeMonth = next.month
          this.currentMonth = tl[next.year].find(m => m.time === next.time)
          this.loadContent(this.currentMonth)
          return
        }
      }
      // No next month, wrap to first visible
      for (let i = 0; i < allMonths.length; i++) {
        if (!allMonths[i].deleted && allMonths[i].time !== curTime) {
          const next = allMonths[i]
          this.activeYear = next.year
          this.activeMonth = next.month
          this.currentMonth = tl[next.year].find(m => m.time === next.time)
          this.loadContent(this.currentMonth)
          return
        }
      }
      // No visible months left
      this.items = []
      this.currentMonth = null
    },
    deleteItem (item) {
      const { sid } = this.$route.query
      const scid = this.getScidForItem(item)
      if (!scid) return
      const type = item._source === 'patch' ? 'patch' : 'facebook'
      api.handleChapterItem(sid, scid, { remove: [item.itemId], type }).then(() => {
        const index = findIndex(this.items, { itemId: item.itemId })
        if (index > -1) {
          this.deletedItems.push(this.items[index])
          this.items.splice(index, 1)
        }
        this.contentChanged = true
        this.$toast({ message: this.$t('content.toastSaveDone'), position: 'bottom' })
        // 当前月所有 posts 都删了
        if (!this.items.length) {
          if (this.deletedItems.length) {
            // 还有 deleted items，切到 deleted 视图
            this.viewMode = 'deleted'
          } else {
            // deleted 也没有了，跳到下个月
            this.switchToNextVisibleMonth()
          }
        }
      })
    },
    saveItem (data, item) {
      const { sid } = this.$route.query
      const scid = this.getScidForItem(item)
      if (!scid) return
      this.activeItemId = ''
      api.handleItemContent(sid, scid, item.itemId, data).then(() => {
        this.contentChanged = true
        this.$toast({ message: this.$t('content.toastSaveDone'), position: 'bottom' })
      }).catch(err => {
        console.error('Save item failed:', err)
        this.$toast({ message: 'Save failed', position: 'bottom' })
      })
    },
    // Batch delete
    enterBatchMode () {
      this.batchMode = true
      this.selectedItems = new Set()
    },
    cancelBatch () {
      this.batchMode = false
      this.selectedItems = new Set()
    },
    toggleSelect (itemId) {
      const s = new Set(this.selectedItems)
      if (s.has(itemId)) {
        s.delete(itemId)
      } else {
        s.add(itemId)
      }
      this.selectedItems = s
    },
    async batchDelete () {
      if (!this.selectedItems.size) return
      const { sid } = this.$route.query
      if (!this.currentMonth) return
      const info = this.socialChapters[this.currentMonth.time]
      if (!info) return
      // Group selected items by source type
      const fbIds = []
      const patchIds = []
      this.items.forEach(item => {
        if (!this.selectedItems.has(item.itemId)) return
        if (item._source === 'patch') {
          patchIds.push(item.itemId)
        } else {
          fbIds.push(item.itemId)
        }
      })
      const promises = []
      if (fbIds.length && info.fb) {
        promises.push(api.handleChapterItem(sid, info.fb, { remove: fbIds, type: 'facebook' }))
      }
      if (patchIds.length && info.patch) {
        promises.push(api.handleChapterItem(sid, info.patch, { remove: patchIds, type: 'patch' }))
      }
      if (!promises.length) return
      await Promise.all(promises)
      const removed = this.items.filter(item => this.selectedItems.has(item.itemId))
      this.deletedItems = this.deletedItems.concat(removed)
      this.items = this.items.filter(item => !this.selectedItems.has(item.itemId))
      this.cancelBatch()
      this.contentChanged = true
      this.$toast({
        message: this.$t('editor.deleteRecoverTip'),
        position: 'bottom'
      })
    },
    recoverItem (item) {
      const { sid } = this.$route.query
      const scid = this.getScidForItem(item)
      if (!scid) return
      const type = item._source === 'patch' ? 'patch' : 'facebook'
      api.handleChapterItem(sid, scid, { recover: [item.itemId], type }).then(() => {
        const index = findIndex(this.deletedItems, { itemId: item.itemId })
        if (index > -1) {
          this.items.push(this.deletedItems[index])
          this.items = orderBy(this.items, ['postDate'], ['asc'])
          this.deletedItems.splice(index, 1)
        }
        this.contentChanged = true
        this.$toast({ message: this.$t('content.toastSaveDone'), position: 'bottom' })
        // 所有 deleted 恢复完了，跳回 posts 视图
        if (!this.deletedItems.length) {
          this.viewMode = 'posts'
        }
      })
    },
    async batchRecover () {
      if (!this.selectedItems.size) return
      const { sid } = this.$route.query
      if (!this.currentMonth) return
      const info = this.socialChapters[this.currentMonth.time]
      if (!info) return
      const fbIds = []
      const patchIds = []
      this.deletedItems.forEach(item => {
        if (!this.selectedItems.has(item.itemId)) return
        if (item._source === 'patch') patchIds.push(item.itemId)
        else fbIds.push(item.itemId)
      })
      const promises = []
      if (fbIds.length && info.fb) {
        promises.push(api.handleChapterItem(sid, info.fb, { recover: fbIds, type: 'facebook' }))
      }
      if (patchIds.length && info.patch) {
        promises.push(api.handleChapterItem(sid, info.patch, { recover: patchIds, type: 'patch' }))
      }
      if (!promises.length) return
      await Promise.all(promises)
      const recovered = this.deletedItems.filter(item => this.selectedItems.has(item.itemId))
      this.items = orderBy(this.items.concat(recovered), ['postDate'], ['asc'])
      this.deletedItems = this.deletedItems.filter(item => !this.selectedItems.has(item.itemId))
      this.cancelBatch()
      this.contentChanged = true
      this.$toast({ message: this.$t('content.toastSaveDone'), position: 'bottom' })
      if (!this.deletedItems.length) {
        this.viewMode = 'posts'
      }
    },
    // Inline add
    startAdding (index) {
      this.addingAt = index
      this.patchContent = ''
      this.patchImages = []
      // Default to previous item's postDate + 1 minute
      const prevItem = index > 0 ? this.items[index - 1] : null
      let d
      if (prevItem && prevItem.postDate) {
        d = new Date(prevItem.postDate)
        d.setMinutes(d.getMinutes() + 1)
      } else if (this.currentMonth && this.currentMonth.time) {
        // 顶部插入：当月第 1 日 00:00
        const t = this.currentMonth.time
        d = new Date(
          parseInt(t.substr(0, 4), 10),
          parseInt(t.substr(4, 2), 10) - 1,
          1, 0, 0, 0
        )
      } else {
        d = new Date()
      }
      const month = `0${d.getMonth() + 1}`.substr(-2)
      const day = `0${d.getDate()}`.substr(-2)
      this.patchDate = `${d.getFullYear()}-${month}-${day}`
      this.patchTime = `0${d.getHours()}`.substr(-2) + ':' + `0${d.getMinutes()}`.substr(-2)
    },
    // Timeline 底部"补写内容"按钮触发：
    // - 视口在顶部 / 当月无内容 → 在最上面插入（index=0，时间 = 当月第 1 日 00:00）
    // - 滚动到某条 → 在"当前可见的那条"之后插入（index = i+1，时间 = 那条 +1 分钟）
    smartAddPatch () {
      if (!this.items || !this.items.length) {
        this.startAdding(0)
        this.$nextTick(() => this._scrollToPatch())
        return
      }
      const list = this.$refs.contentList
      if (!list) {
        this.startAdding(0)
        this.$nextTick(() => this._scrollToPatch())
        return
      }
      const cards = list.querySelectorAll('.editor-card')
      const listTop = list.getBoundingClientRect().top
      let insertAfter = -1
      for (let i = 0; i < cards.length; i++) {
        const rect = cards[i].getBoundingClientRect()
        const relTop = rect.top - listTop
        const relBottom = rect.bottom - listTop
        // 该 card 跨越视口顶部 → 用户正在看它 → 在它之后插
        if (relTop < 0 && relBottom > 0) {
          insertAfter = i
          break
        }
        // card 顶部已在视口内 → 还没找到跨顶部的，说明视口在顶 → 插到最前
        if (relTop >= 0) {
          insertAfter = -1
          break
        }
      }
      if (insertAfter === -1) {
        this.startAdding(0)
      } else {
        this.startAdding(insertAfter + 1)
      }
      this.$nextTick(() => this._scrollToPatch())
    },
    _scrollToPatch () {
      const list = this.$refs.contentList
      if (!list) return
      const patch = list.querySelector('.inline-patch')
      if (patch && patch.scrollIntoView) {
        patch.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    },
    cancelAdding () {
      this.addingAt = -1
      this.patchContent = ''
      this.patchImages = []
    },
    saveNewItem (index) {
      if (!this.patchContent && !this.patchImages.length) {
        this.$toast({ message: this.$t('editor.emptyContent') })
        return
      }
      const { sid } = this.$route.query
      if (!sid) return

      // Validate date format
      if (!this.patchDate.split('-')[0].match(/^\d{4}$/)) {
        this.$toast({ message: this.$t('selection.tipTimeError') })
        return
      }

      const postDate = `${this.patchDate}T${this.patchTime}`
      const picList = this.patchImages.map(img => img[0])
      const item = {
        title: '',
        content: this.patchContent,
        postDate,
        picList
      }
      // Parse target year/month from the entered date
      const targetYear = this.patchDate.split('-')[0]
      const targetMonth = this.patchDate.split('-')[1]
      const targetTime = `${targetYear}${targetMonth}`

      // 先快照当前补写状态，cancelAdding 会清空它们
      const insertIndex = this.addingAt
      const snapshotContent = this.patchContent
      const snapshotImages = this.patchImages.slice()
      const currentTime = this.currentMonth && this.currentMonth.time

      api.createItemContent(sid, item).then((res) => {
        this.cancelAdding()
        this.contentChanged = true
        this.$toast({ message: this.$t('content.toastSaveDone'), position: 'bottom' })

        if (targetTime === currentTime) {
          // 同月补写 —— 本地 insert 后端返回的 item，UI 立刻显示，不 refresh 整月
          // 后端 createitem 现在返回 serialize(item)；兜底：拿不到就临时造一条
          const returned = (res && res.itemId) ? res : null
          const newItem = returned || {
            itemId: 'tmp_' + Date.now(),
            title: '',
            content: snapshotContent,
            postDate,
            pics: {
              current: snapshotImages.map(img => [img[0], img[1]]),
              deleted: []
            }
          }
          newItem._source = 'patch'
          this.items.splice(insertIndex, 0, newItem)
          this.items = orderBy(this.items, ['postDate'], ['asc'])
        } else {
          // 跨月补写 —— 目标月份不是当前月，走 refresh 切月逻辑
          this.refreshSocialChapters(sid).then(() => {
            this.ensureMonthInTimeline(targetYear, targetMonth, targetTime)
            const newMonth = { month: targetMonth, time: targetTime }
            this.activeYear = targetYear
            this.activeMonth = targetMonth
            this.currentMonth = newMonth
            this.loadContent(newMonth)
          })
        }
      }).catch(() => {
        this.$toast({ message: this.$t('selection.tipUploadFailed'), position: 'bottom' })
      })
    },
    async uploadPatchImage (files) {
      const getOrigin = () => /localhost|m\.ipastbook/g.test(window.location.hostname)
        ? 'https://ipastbook.com' : window.location.origin

      for (const file of files) {
        const form = new FormData()
        const src = file.source || file
        form.append('image', src)
        try {
          const data = await api.uploadImage(form)
          if (data && !data.errors) {
            const origin = getOrigin()
            const url = /http?s:/.test(data.image) ? data.image : `${origin}${data.image}`
            this.patchImages.push([data.imageId, url, false, Date.now(), 0])
          }
        } catch (e) {
          this.$toast({ message: this.$t('selection.tipUploadFailed') })
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
// Slide-up animation
.ce-slide-enter-active {
  transition: opacity 0.3s ease;
  .ce-dialog-wrap { transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1); }
}
.ce-slide-leave-active {
  transition: opacity 0.25s ease;
  .ce-dialog-wrap { transition: transform 0.25s ease-in; }
}
.ce-slide-enter, .ce-slide-leave-to {
  opacity: 0;
  .ce-dialog-wrap { transform: translateX(-50%) translateY(100%); }
}

.content-editor {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 100;
}

.ce-backdrop {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: #ccc;
  z-index: 0;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    bottom: -10%;
    left: -10%;
    width: 45%;
    height: 50%;
    background: radial-gradient(ellipse at center, rgba(200, 180, 240, 0.5) 0%, rgba(180, 210, 255, 0.3) 40%, transparent 70%);
    border-radius: 50%;
    filter: blur(60px);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -10%;
    right: -10%;
    width: 45%;
    height: 50%;
    background: radial-gradient(ellipse at center, rgba(170, 230, 220, 0.42) 0%, rgba(180, 240, 210, 0.22) 40%, transparent 70%);
    border-radius: 50%;
    filter: blur(60px);
  }
}

.ce-dialog-wrap {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% - 40px);
  max-width: 1280px;
  z-index: 2;
  padding-top: 40px;
}

.ce-dialog {
  width: 100%;
  height: 100%;
  display: flex;
  border-radius: 12px 12px 0 0;
  overflow: hidden;
  box-shadow: 0 -4px 30px rgba(0,0,0,0.15);
}

.ce-close {
  position: absolute;
  top: 18px;
  right: -42px;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1px solid rgba(0,0,0,0.15);
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  color: #666;
  z-index: 5;
  transition: all 0.2s;
  &:hover { background: #eee; color: #333; }
}

// Left panel: light gray
.ce-left {
  width: 220px;
  flex-shrink: 0;
  background: #e8e8e8;
  overflow: hidden;

  .ce-left-scroll {
    height: 100%;
    overflow-y: auto;
    padding: 1.25rem 1rem;
    &::-webkit-scrollbar { width: 3px; }
    &::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.15); border-radius: 3px; }
  }

  .ce-preview-item {
    text-align: center;
    margin-bottom: 1.5rem;
    border-radius: 8px;
    padding: 6px;
    transition: background 0.2s;

    &.active {
      background: rgba(81, 45, 202, 0.1);

      .ce-preview-item__label {
        color: $el-color-primary;
        font-weight: 600;
      }
    }

    &__wrap {
      position: relative;
      max-width: 130px;
      margin: 0 auto;
      cursor: pointer;

      :deep(.cover-holder) {
        width: 100%;
      }
    }

    &__hover {
      position: absolute;
      top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0,0,0,0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: opacity 0.2s;
      border-radius: 4px;

      span {
        color: #fff;
        font-size: 12px;
        font-weight: 500;
        padding: 5px 12px;
        border: 1px solid rgba(255,255,255,0.7);
        border-radius: 4px;
        white-space: nowrap;
      }
    }

    &__wrap:hover .ce-preview-item__hover {
      opacity: 1;
    }

    &.active &__wrap:hover .ce-preview-item__hover {
      opacity: 1;
    }

    .ce-preview-link {
      color: #fff;
      font-size: 12px;
      font-weight: 500;
      padding: 5px 12px;
      border: 1px solid rgba(255,255,255,0.7);
      border-radius: 4px;
      white-space: nowrap;
      text-decoration: none;
      display: inline-block;
      &:hover { background: rgba(255,255,255,0.2); }
    }

    &__label {
      font-size: 12px;
      color: $el-color-primary;
      margin-top: 8px;
      font-weight: 500;

      i.fa {
        margin-right: 4px;
        font-size: 11px;
      }
    }
  }
}

// Right panel: white
.ce-right {
  flex: 1;
  background: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

// Body: timeline + content side by side
.ce-body {
  flex: 1;
  display: flex;
  overflow: hidden;
}

// Manage Contents: content sources panel
.ce-sources-panel {
  flex: 1;
  overflow: hidden;

  .ce-sources-scroll {
    height: 100%;
    overflow-y: auto;
    padding: 1.75rem 2rem;
    &::-webkit-scrollbar { width: 4px; }
    &::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.12); border-radius: 4px; }
  }

  // Top bar: connect more
  .ce-sources-connect {
    display: flex;
    align-items: center;
    gap: 1.25rem;

    &__label {
      font-size: 16px;
      font-weight: 600;
      color: #222;
    }

    &__icons { display: flex; gap: 0.625rem; }
  }

  .ce-source-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 10px;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    text-decoration: none;
    color: #fff;
    transition: all 0.2s;
    background-color: #555;

    &:hover { opacity: 0.85; }
    &--fb { background-color: #1877F2; border-radius: 50%; }
    &--ins { background-color: #333; span { font-size: 13px; } }
    &--x { background-color: #000; }
    &--disabled { opacity: 0.35; cursor: not-allowed; &:hover { opacity: 0.35; } }

    .ce-source-icon__img {
      width: 18px;
      height: 18px;
      object-fit: contain;
      filter: invert(1); // SVG 原图黑色，放到黑底上翻白
    }
  }

  .ce-sources-divider {
    border: none;
    border-top: 1px solid #eee;
    margin: 1.5rem 0;
  }

  // Card grid
  // ============== 新版两栏布局：左 source 列表 + 右 month grid ==============
  .ce-sources-layout {
    display: flex;
    gap: 1.25rem;
    align-items: flex-start;
    @media (max-width: 800px) { flex-direction: column; }
  }
  .ce-sources-list {
    width: 320px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 0.875rem;
    @media (max-width: 800px) { width: 100%; }
  }
  .ce-months-pane {
    flex: 1;
    min-width: 0;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    background: #fafafa;
    padding: 16px;
    min-height: 300px;
    &__empty {
      color: #9ca3af; padding: 60px 20px; text-align: center; font-size: 14px;
    }
    &__header {
      display: flex; justify-content: space-between; align-items: center;
      margin-bottom: 14px; flex-wrap: wrap; gap: 8px;
    }
    &__hint {
      color: #9ca3af; font-size: 12px; margin-left: 8px;
    }
    &__toggle-all {
      cursor: pointer;
      color: $el-color-primary;
      font-size: 13px;
      font-weight: 500;
      user-select: none;
      &:hover { opacity: 0.8; }
    }
    &__actions {
      display: flex; gap: 8px; align-items: center;
    }
    &__diff {
      font-size: 12px; color: #f56368; font-weight: 600;
      &.is-empty { color: #9ca3af; font-weight: 400; }
    }
    &__loading {
      text-align: center; padding: 40px;
    }
  }
  .ce-years-grid {
    display: flex; flex-direction: column; gap: 14px;
  }
  .ce-year-block {
    background: #fff; border: 1px solid #eee; border-radius: 10px;
    padding: 10px 14px;
    &__count { color: #9ca3af; font-style: normal; font-weight: normal; font-size: 12px; margin-left: 6px; }
  }
  .ce-year-checkbox {
    display: flex; align-items: center; gap: 8px; cursor: pointer;
    font-weight: 600; font-size: 14px; color: #1a1a2e; margin-bottom: 8px;
    input { width: 16px; height: 16px; cursor: pointer; }
  }
  .ce-months-row {
    display: flex; flex-wrap: wrap; gap: 6px;
  }
  .ce-month-chip {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 6px 12px; border: 1px solid #e5e7eb; border-radius: 8px;
    cursor: pointer; font-size: 13px; background: #fff;
    color: #444; transition: all .12s;
    input { display: none; }
    &__label { font-weight: 500; }
    &__sep { color: #d0d0d0; }
    &__count { color: #9ca3af; font-size: 12px; }
    &:hover { border-color: #d1d5db; background: #fafafa; }
    &.checked {
      background: rgba(245, 99, 104, 0.08);
      border-color: $el-color-primary;
      color: $el-color-primary;
      .ce-month-chip__sep { color: rgba(245, 99, 104, .35); }
      .ce-month-chip__count { color: $el-color-primary; opacity: .8; }
    }
  }

  // 旧 layout 兼容（左侧仍然用 .ce-source-card 样式）
  .ce-source-cards {
    display: flex;
    flex-wrap: wrap;
    gap: 1.25rem;
    align-items: flex-start;
  }

  .ce-source-card {
    position: relative;
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    min-width: 280px;
    max-width: 380px;
    flex: 1;
    background: #fff;
    transition: all .15s;

    // Corner badge (f / P)
    &__badge {
      position: absolute;
      top: -14px;
      left: 16px;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      font-weight: 700;
      color: #fff;
      z-index: 1;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);

      &--fb { background-color: #1877F2; }
      &--patch { background: $el-color-primary; }
      &--x { background-color: #000; }
    }

    &__badge-img {
      width: 18px;
      height: 18px;
    }

    &__badge--x &__badge-img,
    &__badge--x .ce-source-card__badge-img {
      filter: invert(1); // X logo 原本是黑色，放在黑色底上要翻白
    }

    &__body {
      padding: 1.25rem 1.25rem 1rem;
      padding-top: 1.5rem;
    }

    // Header: avatar + name + date + action btn
    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.75rem;
      min-height: 40px;
    }

    &__user {
      display: flex;
      align-items: center;
      gap: 0.625rem;
      flex: 1;
      min-width: 0;
    }

    &__avatar {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      object-fit: cover;
      border: 1px solid #e0e0e0;
      flex-shrink: 0;
    }

    &__avatar--fb-fallback {
      background: #1877F2;
      border-color: #1877F2;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &__name {
      font-size: 14px;
      font-weight: 600;
      color: #222;
      display: block;
      line-height: 1.3;
    }

    &__meta {
      font-size: 12px;
      color: #999;
      line-height: 1.3;
    }

    &__action {
      flex-shrink: 0;
      padding: 5px 14px;
      border: 1px solid #d0d0d0;
      border-radius: 6px;
      background: #fff;
      font-size: 12px;
      color: #555;
      cursor: pointer;
      transition: all 0.2s;
      white-space: nowrap;

      &:hover {
        background: $gradient-purple-blue;
        border-color: transparent;
        color: #fff;
      }
    }

    &__info {
      flex: 1;
      min-width: 0;
    }

    &__divider {
      border: none;
      border-top: 1px solid #f0f0f0;
      margin: 1rem 0;
    }

    // Category items stacked vertically
    &__categories {
      display: flex;
      flex-direction: column;
      gap: 0.625rem;
    }
  }

  // Category item (inside card)
  .ce-source-category {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.75rem 1rem;
    border: 1.5px solid #e0e0e0;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.2s;
    background: #fafafa;

    // hover 也走红框 —— 用户要的：红框只表达"正在操作"的态（hover/active）
    &:hover {
      border-color: $el-color-primary;
      background: rgba($el-color-primary, 0.04);
    }

    // 当前在右侧网格里查看的那个 category：红框 + 浅底
    &--active {
      border-color: $el-color-primary;
      background: rgba($el-color-primary, 0.06);
    }

    // 同步后数字闪烁 — 提示用户"这里已更新"
    &__count {
      display: inline-block;
      transition: color 0.2s, transform 0.2s;
      &.is-flashing {
        animation: ce-count-flash 1.2s ease-out;
      }
    }

    // 绿色小圆点 = "本书有这个来源的内容"。无内容时占位透明，
    // 保持卡片宽度对齐。
    &__dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      flex-shrink: 0;
      background: transparent;
      transition: background 0.2s;
      &.is-on { background: #22c55e; }
    }

    &__label {
      font-size: 13px;
      color: #444;
    }

    &__sep {
      margin: 0 0.375rem;
      color: #ccc;
    }
  }
}

// Design editing panels (cover, typeset, insert)
.ce-design-panel {
  flex: 1;
  overflow: hidden;

  .ce-design-scroll {
    height: 100%;
    overflow-y: auto;
    padding: 1.25rem 1.5rem;
    &::-webkit-scrollbar { width: 4px; }
    &::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.12); border-radius: 4px; }

    .form-group {
      margin-bottom: 1rem;
      label {
        display: block;
        font-size: 12px;
        font-weight: 500;
        color: #666;
        margin-bottom: 4px;
      }
      .form-control {
        border-radius: 6px;
        border: 1px solid #e0e0e0;
        padding: 7px 10px;
        font-size: 13px;
        &:focus { border-color: $el-color-primary; outline: none; box-shadow: 0 0 0 2px rgba(81,45,202,0.08); }
      }
    }

    // Top area: left 50% fields, right 50% cover image
    .ce-cover-top {
      display: flex;
      gap: 20px;
      margin-bottom: 1rem;

      &__left {
        width: 50%;
        flex-shrink: 0;
      }

      &__right {
        width: 50%;
        display: flex;
        flex-direction: column;
        align-items: flex-start;

        > label {
          font-size: 12px;
          font-weight: 500;
          color: #666;
          margin-bottom: 6px;
        }
      }
    }

    .ce-cover-thumb {
      position: relative;
      width: 60%;
      padding-bottom: 30%;
      cursor: pointer;
      border-radius: 6px;
      overflow: hidden;
      box-shadow: 0 2px 8px rgba(0,0,0,0.12);
      background: #f5f5f5;

      &__img {
        position: absolute;
        top: 0; left: 0; right: 0; bottom: 0;
        background-size: cover;
        background-position: center;
      }

      &__hover {
        position: absolute;
        top: 0; left: 0; right: 0; bottom: 0;
        background: rgba(0,0,0,0.5);
        opacity: 0;
        transition: opacity 0.2s;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 6px;
        z-index: 2;

        ::v-deep .uploader {
          display: block;
          height: auto;
        }

        .ce-hover-btn {
          color: #fff;
          font-size: 11px;
          font-weight: 500;
          padding: 5px 12px;
          border: 1px solid rgba(255,255,255,0.6);
          border-radius: 4px;
          white-space: nowrap;
          cursor: pointer;
          display: inline-block;
          transition: background 0.15s;
          i { margin-right: 4px; }
          &:hover { background: rgba(255,255,255,0.15); }

          &--reset {
            position: relative;
            z-index: 2;
          }
        }
      }

      &:hover .ce-cover-thumb__hover {
        opacity: 1;
      }
    }

    .ce-panel-title {
      font-size: 15px;
      font-weight: 600;
      color: #333;
      margin-bottom: 6px;
    }
    .ce-panel-subtitle {
      font-size: 12px;
      color: #999;
      margin-bottom: 16px;
    }

    .ce-manage-section {
      padding: 1.25rem 0;
      & + .ce-manage-section {
        border-top: 1px solid #eee;
      }
      &__title {
        font-size: 14px;
        font-weight: 600;
        color: #333;
        margin-bottom: 12px;
        display: flex;
        align-items: center;
        gap: 6px;
        i.fa { color: $el-color-primary; font-size: 13px; }
      }
    }

    // Cover type grid — 20px gap + hover depth effect
    ::v-deep .cover-type {
      .clearfix { margin-bottom: 8px; }
      .clearfix label { font-size: 12px !important; font-weight: 500 !important; color: #666 !important; }
      .clearfix .pull-right { display: none !important; }
      .text-mini { font-size: 11px; }
      .cover-types { gap: 0; }
      .cover-type-item {
        width: 25% !important;
        flex: 0 0 25% !important;
        padding: 30px 10px !important;

        .cover-holder {
          transition: transform 0.2s, box-shadow 0.2s;
          border-radius: 2px 4px 4px 2px;
        }
        &:hover .cover-holder {
          transform: scale(1.03);
          box-shadow:
            4px 4px 8px rgba(0,0,0,0.18),
            1px 0 0 #c8c4bf,
            2px 0 0 #d5d1cc,
            3px 0 0 #e2dedb;
        }
      }
    }

    // Color list — compact
    ::v-deep .color-list {
      .color-item {
        width: 28px !important;
        height: 28px !important;
        margin: 3px !important;
      }
    }

    // Typeset — two groups with gap, 80% scale
    ::v-deep .typeset-type {
      .group {
        padding: 0 !important;
        border: none !important;
        margin-bottom: 40px !important;
        &:last-child { margin-bottom: 0 !important; }
      }
      .group label {
        display: block !important;
        font-size: 13px !important;
        font-weight: 600 !important;
        color: #333 !important;
        margin-bottom: 12px !important;
      }
      .typeset-type-item {
        width: 30% !important;
        flex: 0 0 30% !important;
        padding: 8px !important;
        p { font-size: 12px !important; margin-top: 6px !important; text-align: center; }
        &.A4 {
          width: 30% !important;
          flex: 0 0 30% !important;
        }
      }
    }

    // Insert page
    ::v-deep .insert-page {
      padding: 10px 0;
    }
  }
}

.ce-timeline {
  width: 140px;
  flex-shrink: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0.75rem 0;
  border-right: 1px solid #eee;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  &::-webkit-scrollbar { width: 3px; }
  &::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.12); border-radius: 3px; }

  :deep(.timeline) {
    text-align: right;
  }
}

// Tabs: no borders, active = bottom border
.ce-tabs {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1.5rem;
  border-bottom: 1px solid #eee;
  flex-shrink: 0;

  &__list {
    display: flex;
    align-items: center;
    gap: 0;
  }

  .ce-tab {
    padding: 18px 20px;
    font-size: 14px;
    font-weight: 500;
    color: #666;
    cursor: pointer;
    border: none;
    border-bottom: 2px solid transparent;
    background: none;
    transition: all 0.2s;
    margin-bottom: -1px;
    display: flex;
    align-items: center;
    gap: 5px;

    .ce-tab-icon { vertical-align: middle; }

    &:hover { color: $el-color-primary; }
    &.active {
      color: $el-color-primary;
      font-weight: 600;
      border-bottom-color: $el-color-primary;
    }
  }

  .ce-save {
    font-size: 13px;
    padding: 8px 20px;
    border-radius: 6px;
  }
}

.ce-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;

  .content-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 25% 0.75rem 1.5rem;
    border-bottom: none;
    flex-shrink: 0;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 1.5rem;
      right: 25%;
      height: 1px;
      background: #eee;
    }

    &__info {
      display: flex;
      align-items: center;
      gap: 12px;
      font-size: 16px;
      strong { font-weight: 700; }
    }

    .content-tab {
      font-size: 13px;
      color: #999;
      cursor: pointer;
      padding: 2px 8px;
      border-radius: 4px;
      transition: color 0.2s, background 0.2s;
      &:hover { color: #666; background: #f5f5f5; }
      &.active { color: $el-color-primary; font-weight: 600; }
      &--deleted { color: #ef4444; }
      &--deleted:hover { color: #dc2626; background: #fef2f2; }
      &--deleted.active { color: #ef4444; font-weight: 600; }
    }

    &__actions {
      display: flex;
      align-items: center;
      gap: 12px;

      .action-link {
        font-size: 13px;
        color: #999;
        cursor: pointer;
        padding: 4px 10px;
        border: 1px solid transparent;
        border-radius: 4px;
        transition: all 0.2s;

        i { margin-right: 4px; }
        &:hover { color: #e53935; border-color: #e53935; }
        &.danger { color: #e53935; &:hover { border-color: #e53935; } }
      }
    }
  }

  .content-list {
    flex: 1;
    overflow-y: auto;
    padding: 1.25rem 1.5rem;
    padding-right: 25%;
    &::-webkit-scrollbar { width: 4px; }
    &::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.12); border-radius: 4px; }
  }

  .content-loading {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .content-empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #ccc;
    i.fa { font-size: 3rem; margin-bottom: 1rem; }
    p { font-size: 14px; }
  }
}

// Add divider with + button
.add-divider {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 4px 0;
  position: relative;

  &__line {
    display: none;
  }

  &__btn {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: none;
    background: $el-color-primary;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 16px;
    flex-shrink: 0;
    transition: all 0.25s;
    position: relative;
    margin-right: -30px;
    opacity: 0;

    &::before {
      content: attr(title);
      position: absolute;
      right: 40px;
      white-space: nowrap;
      background: #333;
      color: #fff;
      font-size: 12px;
      padding: 4px 10px;
      border-radius: 4px;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.2s;
    }

    .add-divider:hover & {
      opacity: 1;

      &::before {
        opacity: 1;
      }
    }

    &:hover {
      transform: scale(1.1);
      box-shadow: 0 2px 8px rgba(81, 45, 202, 0.4);
    }
  }
}

// Inline patch form
.inline-patch {
  margin-top: 20px;
  border: 2px solid $el-color-primary;
  border-radius: 8px;
  background: #fff;
  margin-bottom: 1rem;
  overflow: hidden;

  &__header {
    padding: 10px 16px;
    font-size: 14px;
    font-weight: 600;
    color: $el-color-primary;
    border-bottom: 1px solid #eee;
  }

  &__body {
    padding: 12px 16px;

    textarea {
      margin: 8px 0;
      min-height: 80px;
      resize: vertical;
    }
  }

  &__footer {
    display: flex;
    justify-content: space-between;
    padding: 10px 16px;
    border-top: 1px solid #eee;
    background: #fafafa;
  }

  &__images {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin: 8px 0;
  }

  .patch-img {
    width: 80px;
    height: 80px;
    border-radius: 6px;
    position: relative;
    overflow: hidden;
    cursor: pointer;

    &__inner {
      width: 100%; height: 100%;
      background-size: cover;
      background-position: center;
    }

    &__del {
      position: absolute;
      top: 0; right: 0;
      width: 20px; height: 20px;
      background: rgba(0,0,0,0.5);
      border-radius: 0 0 0 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      font-size: 10px;
    }

    &.upload-btn {
      border: 1px dashed #ccc;
      background: #fafafa url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUAQMAAAC3R49OAAAABlBMVEUAAADd3d3swgqXAAAAAXRSTlMAQObYZgAAABZJREFUCFtjZPggwIgP/2f4iIIJqAcAuP0X1ZQ/+6oAAAAASUVORK5CYII=) center no-repeat;

      label {
        position: absolute;
        top: 0; left: 0; width: 100%; height: 100%;
        cursor: pointer;
      }
    }
  }
}

// ============ MOBILE NAV ============
.ce-mobile-nav {
  display: none !important;
}

.ce-mobile-toc {
  display: none !important;
}

.ce-mobile {
  .ce-left { display: none !important; }
  .ce-tabs { display: none !important; }
  .ce-timeline { display: none !important; }

  .ce-dialog-wrap {
    width: 100% !important;
    padding-top: 0 !important;
    top: 0 !important;
    bottom: 0 !important;
    transform: translateX(-50%) !important;
  }

  .ce-close {
    top: 6px !important;
    right: 6px !important;
    width: 28px !important;
    height: 28px !important;
    font-size: 14px !important;
  }

  .ce-dialog {
    flex-direction: column !important;
    border-radius: 0 !important;
  }

  .ce-right {
    width: 100% !important;
  }

  .ce-mobile-nav {
    display: flex !important;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    border-bottom: 1px solid #eee;
    background: #fff;
    position: relative;
    z-index: 5;

    &__left, &__right {
      position: relative;
    }

    &__btn {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 6px 12px;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      background: #fff;
      font-size: 13px;
      font-weight: 500;
      color: #374151;
      cursor: pointer;

      i { font-size: 12px; }
    }

    &__overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 9;
    }

    &__pop {
      position: absolute;
      top: 0;
      background: #fff;
      border-radius: 8px;
      box-shadow: 0 4px 20px rgba(0,0,0,0.15);
      overflow: hidden;
      z-index: 10;
      white-space: nowrap;

      &--left { left: 100%; margin-left: 6px; }
      &--right { right: 100%; margin-right: 6px; }

      button {
        display: block;
        width: 100%;
        padding: 10px 18px;
        border: none;
        background: none;
        font-size: 13px;
        color: #374151;
        text-align: left;
        cursor: pointer;

        &:active { background: #f3f4f6; }
        & + button { border-top: 1px solid #f3f4f6; }
      }
    }

    .ce-mobile-save {
      padding: 6px 14px;
      font-size: 12px;
      border-radius: 6px;
    }
  }

  .ce-mobile-toc {
    display: flex !important;
    position: fixed;
    left: 16px;
    bottom: 50px;
    z-index: 110;
    flex-direction: column;
    align-items: flex-start;

    &__title {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 6px;
      padding: 10px 16px 8px;
      font-size: 13px;
      font-weight: 600;
      color: #1a1a2e;
      border-bottom: 2px solid $el-color-primary;
      margin: 0 8px 6px;
    }

    &__panel {
      background: #fff;
      border-radius: 12px;
      box-shadow: 0 8px 32px rgba(0,0,0,0.15);
      margin-bottom: 12px;
      max-height: calc(100vh - 180px);
      max-height: calc(100dvh - 140px);
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      overscroll-behavior: contain;
      touch-action: pan-y;
      transform: translateZ(0);
      position: relative;
      z-index: 110;
      padding: 6px 0;
      min-width: 160px;
      box-sizing: border-box;

      .timeline {
        display: block !important;
        width: 100%;
        text-align: right;
        .tl-action-btn { display: none !important; }
        .year-row { justify-content: flex-end; }
        .year-label { flex: none !important; width: 80px !important; text-align: center !important; box-sizing: border-box !important; }
        .months { text-align: right; }
        .months li { justify-content: flex-end; }
        .months li a { flex: none !important; text-align: right !important; }
      }
    }

    &__overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 109;
    }

    &__fab {
      width: 44px;
      height: 44px;
      border-radius: 50%;
      border: none;
      background: $el-color-primary;
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 111;
      box-shadow: 0 4px 16px rgba(81,45,202,0.35);
      cursor: pointer;
    }
  }

  // Content panel full-width, no horizontal scroll
  .ce-body { overflow-x: hidden !important; }
  .content-header { padding: 0.75rem 12px !important; }
  .content-list { padding: 0 12px !important; overflow-x: hidden !important; }
  .content-empty { padding: 2rem 12px !important; }
  .editor-card { overflow: hidden; }

  // 移动端：补录按钮回到容器内，避免被屏幕边缘截掉，并默认显示
  .add-divider__btn {
    margin-right: 0 !important;
    opacity: 1 !important;

    &::before {
      right: 44px !important;
    }
  }

  // Design panel responsive
  .ce-design-scroll { padding: 1rem !important; }
  .ce-cover-top {
    flex-direction: row !important;
    flex-wrap: wrap !important;
    gap: 10px !important;
    &__preview { width: 38% !important; flex: 0 0 38% !important; order: 1; }
    &__left { width: 55% !important; flex: 0 0 55% !important; order: 2; }
    &__right { display: none !important; }
  }

  .ce-cover-bottom-row {
    display: flex;
    gap: 12px;
    margin-top: 0.5rem;

    label { display: block; font-size: 12px; font-weight: 500; color: #666; margin-bottom: 4px; }

    &__color {
      flex: 1;
      min-width: 0;

      .color-list li {
        height: 28px !important;
        width: 28px !important;
        margin-right: 6px !important;
        margin-bottom: 6px !important;
      }
    }

    &__photo {
      flex: 0 0 80px;

      .ce-cover-thumb--mobile {
        width: 80px;
        height: 56px;
        padding-bottom: 0;
        position: relative;
        border-radius: 6px;
        overflow: hidden;
        box-shadow: 0 2px 8px rgba(0,0,0,0.12);
        background: #f5f5f5;
        cursor: pointer;

        .ce-cover-thumb__img {
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          background-size: cover;
          background-position: center;
        }
        .ce-cover-thumb__hover {
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.2s;
        }
        &:hover .ce-cover-thumb__hover { opacity: 1; }
      }
    }
  }

  // Sources panel responsive
  .ce-source-cards { flex-direction: column !important; }
  .ce-source-card { width: 100% !important; }
}

@keyframes ce-count-flash {
  0%   { color: inherit; transform: scale(1); }
  15%  { color: #10b981; transform: scale(1.35); } /* 绿色高亮 + 放大 */
  35%  { color: #10b981; transform: scale(1.25); }
  100% { color: inherit; transform: scale(1); }
}

.sync-progress {
  text-align: center;
  padding: 24px 8px 8px;

  &__icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: #1877f2; // FB blue
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
    box-shadow: 0 4px 16px rgba(24, 119, 242, 0.3);
    transition: background 0.3s;

    &--done {
      background: #22c55e;
      box-shadow: 0 4px 16px rgba(34, 197, 94, 0.3);
    }
  }

  &__title {
    font-size: 18px;
    font-weight: 600;
    color: #1a1a2e;
    margin: 0 0 8px;
  }

  &__desc {
    font-size: 14px;
    color: #6b7280;
    margin: 0 0 20px;
    min-height: 20px;

    &--result {
      color: #1a1a2e;
      font-weight: 500;
      font-size: 15px;
    }
  }

  &__bar {
    width: 100%;
    height: 6px;
    background: #f0f0f5;
    border-radius: 3px;
    overflow: hidden;
    margin-bottom: 8px;
  }

  &__fill {
    height: 100%;
    background: linear-gradient(90deg, #1877f2, #4f9bff);
    border-radius: 3px;
    transition: width 0.3s ease;
  }

  &__close {
    display: block;
    margin: 16px auto 0;
    min-width: 140px;
  }
}
</style>
